import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import {DetectMethodChanges, getShortUUID} from '../utils';
import {OptionExpirationDescriptor} from '../shell-communication/shell-dto-protocol';
import {TradingInstrument} from '../trading-instruments/trading-instrument.class';
import {
    adoptPreLoadBaseline,
    ALL_DATES_INDEX,
    distinctUnpreviewedExpirationCount,
    driftedGridMembers,
    DualSideTabInputs,
    isDualSideInputs,
    copyOverride,
    isOverrideComplete,
    isUniformAcrossDates,
    makeGridFingerprint,
    OpgAtmOffsetSub,
    makeEmptyOverride,
    MarketConditionOverride,
    MarketConditionOverrides,
    OverrideChangedKeys,
    readOverrideChangedKeys,
    OpgAtmRangeValue,
    OpgDateSelection,
    OpgGridFingerprint,
    OpgGridMember,
    OpgMaxRisk,
    OpgPriceRange,
    OpgSide,
    OpgTradeSuggestion,
    relevantGridMembers,
    RunContents,
    SideInputs,
    SideResult,
    SideTradeContents,
    sideUsesAtmOffset,
    sideUsesExpectedRange,
    SingleSideTabInputs,
    Tab,
    TabInputs,
    TabKind,
    unPreviewedMembers,
    withExpiration
} from './opg-copilot-domain';
import {Subscription} from 'rxjs';
import {IvCacheService} from '../iv-cache.service';
import {MessageBusService} from '../message-bus.service';
import {ShellClientService} from '../shell-communication/shell-client.service';
import {HorizontalSkewService} from '../horizontal-skew/horizontal-skew.service';
import {ExpectedMoveService} from '../expected-move/expected-move.service';
import {AppSettingsUpdatedMessageTopic} from '../app-settings/application-settings.service';
import {
    SubscribeVerticalSkew,
    UnsubscribeVerticalSkew,
    VerticalSkewChangedDto
} from '../skew-meter/skew-meter-cqrs.model';

// How often the IV box re-asks, ms. Matched to IvCacheService's own TTL: asking faster cannot
// observe anything new (it would just replay the cached reply), and asking slower would make the
// box visibly lag the panel's header, which reads the same source.
const LIVE_IV_POLL_MS = 2000;

// 'yyyy-MM-dd' from LOCAL calendar components (CR-20260722-002 override keys).
// backend-opg-trades-skill.ts holds its own module-private copy for the wire mapping; the two MUST
// agree, because this function produces the keys the adapter then sends as override dates. Kept
// local rather than shared to avoid the orchestrator importing from the transport.
// Best-effort error text for a seed-failure log line. A rejected shell query can carry its detail
// on .message, on .Message (server-shaped), or be a bare string.
function describeError(e: any): string {
    if (!e) {
        return 'unknown error';
    }
    if (typeof e === 'string') {
        return e;
    }
    return String(e.message || e.Message || e);
}

function toDateKey(d: Date): string {
    const m = d.getMonth() + 1;
    const day = d.getDate();
    return d.getFullYear() + '-' + (m < 10 ? '0' + m : '' + m) + '-' + (day < 10 ? '0' + day : '' + day);
}
import {OpgCopilotSession, SettledOutcome} from './opg-copilot-session';
import {diffOverrides, diffTabInputs, hasAnyChange, ParamChangeMap, subMap} from './opg-copilot-param-diff';
import {isTabComplete} from './opg-copilot-questionnaire';
import {IOpgTradesSkill, OPG_TRADES_SKILL, OpgSkillRequest, OpgSkillSideResult} from './opg-trades-skill';
import {IOpgCopilotFacade} from './opg-copilot-facade';
import {GenericConfirmationDialogComponent} from '../generic-confirmation-dialog/generic-confirmation-dialog.component';

// The OPG CoPilot ORCHESTRATOR (CR-20260710-001, phase 1: shell + mock) — the content inside the
// host's dx-popup window. Owns the session, the active tab, the per-tab working questionnaire
// inputs, the Run flow (verbatim courier, §6), and the Preview/Un-Preview facade calls (the Hedge
// preview model: REAL strategies via loadTrade/unloadTrade + the "X marks the spot" border, §7/§8).
//
// A5 (owner meeting 2, 2026-07-13): trades are DATE-BOUND (each names its expiration). WI-6:
// previewing a trade bound to ANOTHER date auto-un-previews every other-date preview, then
// switches the panel to the trade's date (root.navigateToExpiration) before loading — the only
// expiration change the CoPilot causes. Same-date preview loads in place (sticky comparison, §7).
//
// ISOLATED SIBLING: structure duplicated from the APG CoPilot (host-popup-content pattern, root
// facade input, ResizeObserver height reporting) and the Hedge CoPilot (run lifecycle + outcome
// handling) WITHOUT importing either folder (family rule).
//
// ES5 note: arrays + indexOf only (no Set/Map iteration, no for..of), matching the sibling logic.

// The per-tab working questionnaire inputs. Calls/Puts carry one date + one side; Calls & Puts
// carries the ONE shared date + BOTH sides (§5 — the panel's one-base-date rule).
interface WorkingInputs {
    calls: SingleSideTabInputs;
    puts: SingleSideTabInputs;
    callsAndPuts: DualSideTabInputs;
}

@Component({
    selector: 'ets-opg-copilot',
    templateUrl: './opg-copilot.component.html',
    styleUrls: ['./opg-copilot.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OpgCopilotComponent implements OnInit, AfterViewInit, OnDestroy {

    constructor(
        private readonly _changeDetector: ChangeDetectorRef,
        private readonly _zone: NgZone,
        // CR-20260722-002: seeds the IV box for dates OTHER than the panel's selected expiration.
        // Root-provided and keyed by (underlying, expiration) with a short TTL, so seeding a range
        // coalesces with whatever the panel and its siblings are already fetching.
        private readonly _ivCache: IvCacheService,
        // CR-20260722-002: the one-shot GetVerticalSkew seed. The panel's own VS subscriber
        // self-filters to the SELECTED expiration, so it cannot answer for any other date.
        private readonly _shellClient: ShellClientService,
        private readonly _messageBus: MessageBusService,
        // Provided at the OPG component level, so this resolves the PANEL's instance through the
        // hierarchical injector — the same one whose ratio the header shows. The two cannot disagree.
        private readonly _horizontalSkew: HorizontalSkewService,
        // CR-20260727-001: the expected-move MODEL the run is couriered under, plus the gate that
        // decides whether the Expected Range forms exist at all.
        private readonly _expectedMoveService: ExpectedMoveService,
        @Inject(OPG_TRADES_SKILL) private readonly _skill: IOpgTradesSkill
    ) {
    }

    // FE-only session aggregate — transient, this-client-only (§9). PUBLIC so the template reads
    // isRunInFlight; all mutations still go through this component's handlers.
    readonly session = new OpgCopilotSession(getShortUUID, () => Date.now());

    // The host contract (OptionsPricingGridComponentBase implements it). The child never reaches
    // into the host past this surface.
    @Input() root: IOpgCopilotFacade | null = null;

    // Whether this copilot renders its OWN refresh (New Conversation) button. TRUE in the OPG
    // popup, where it is the only reset. The HedgeMatrix host binds FALSE (owner smoke ruling
    // 2026-08-04: one reset per window) — its window-level New Conversation already recreates
    // this embed, and its header refresh carries the merged stale cue, so nothing is lost.
    @Input() showRefreshButton = true;

    // The host's PREVIEW MODEL — the one deliberate GUI-behaviour divergence between the two
    // hosts (owner ruling 2026-08-04, the two-copilots smoke):
    //   'compare' — the OPG popup's design intent: side-by-side comparison. Same-date previews
    //               are STICKY (§7), and a cross-date preview navigates the single-expiration
    //               panel, auto-un-previewing other dates first (WI-6).
    //   'pick'    — the HedgeMatrix Select tab: the trader picks THE hedge. A preview is
    //               EXCLUSIVE PER (DATE, SIDE) — same-date same-side candidates duplicate each
    //               other's purpose, so the new pick REPLACES the standing one — while
    //               different dates coexist (one pick per expiration builds the book) and the
    //               Load-All comparison bar hides. Nothing navigates: the Matrix shows every
    //               expiration at once, so WI-6 does not apply on this host.
    @Input() previewModel: 'compare' | 'pick' = 'compare';

    // Host-owned popup geometry: natural content height (ResizeObserver, APG donor recipe) and the
    // width mode — 'wide' ONLY on the Calls & Puts tab (§5), 'normal' otherwise.
    @Output() contentHeightChange = new EventEmitter<number>();
    @Output() widthModeChange = new EventEmitter<'normal' | 'wide'>();
    // Reserved for a footer Close button. The sketches show only the dx-popup titlebar close
    // (frame 02 "✕"), so nothing in this content emits it today.
    @Output() closeRequested = new EventEmitter<void>();
    // Stale cue for the collapsed launcher (WI-15/A6): the FAB carries the yellow while the
    // window is closed, because that is the only thing the user can still see. Deduped (emitted on
    // a real transition only) — the HedgeMatrix donor's staleChange, duplicated.
    // CR-20260831-004: a UNION — grid drift OR parameters changed since the tab's current run.
    @Output() staleChange = new EventEmitter<boolean>();

    // CR-20260831-004 (defect round 1, owner ruling): the GRID-DRIFT-ONLY sibling of staleChange,
    // for host surfaces that must ignore param drift — the HedgeMatrix header refresh button
    // reports underlying/book drift only, while its FAB keeps the union above. Deduped separately.
    @Output() gridStaleChange = new EventEmitter<boolean>();

    // CR-20260831-004 round 3: a Run just fired in this copilot. The HedgeMatrix host tracks the
    // window-level last-run engine (Select | Adjust | Close) with it — its own runs it sees
    // directly; a Select run happens inside this embed and would otherwise be invisible to it.
    @Output() runFired = new EventEmitter<void>();

    activeTab: TabKind = 'calls';

    // CR-20260831-004 round 3 (owner recording): the tab of this conversation's LAST run. When the
    // ACTIVE tab differs, the tab bar paints the active segment yellow — "what is selected now is
    // not what has been ran, the results you are looking at". Tab-level only, deliberately: a tab
    // switch highlights the TAB, never the fresh tab's sub-controls (they have no baseline), and
    // an inner change on another tab does not reach up to a tab header (owner Q2). null until the
    // first run; reset by the fresh conversation, so a reset dialog carries no tab cue.
    lastRunTab: TabKind | null = null;

    get activeTabNotLastRun(): boolean {
        return this.lastRunTab !== null && this.activeTab !== this.lastRunTab;
    }

    // Toggled off/on to recreate the questionnaires with fresh inputs on a new conversation
    // (symbol change / reopen after close) — the family recipe.
    contentVisible = true;

    workingInputs: WorkingInputs = this.makeDefaultWorkingInputs();

    // ---- Market conditions: READINGS vs OVERRIDES (CR-20260722-002; owner ruling D20) ---------
    //
    // Two different things, deliberately in two fields. They share one struct because that struct
    // is what the wire adapter already consumes; the INVARIANT is what separates them:
    //
    //   readingsByDate — what the MARKET says. Keyed by DATE (vsCall/vsPut are the two sides of one
    //                    VerticalSkewChangedDto, so a single subscription serves both). SHARED
    //                    ACROSS TABS: IV for 24-Jul is IV for 24-Jul whichever tab is open.
    //                    Always `edited: false`.
    //
    //   overridesByTab — what the USER typed. Keyed by TAB, then date. A tab is an INDEPENDENT
    //                    request, so an edit made on Calls must never show up on Puts (owner ruling
    //                    D20; the previous "shared across tabs" model was an implementation
    //                    assumption, never a requirement). Holds ONLY edited dates —
    //                    always `edited: true`.
    //
    // Display for the anchored date = this tab's override if it has one, otherwise the reading.
    // Hence yellow border/text (CR-20260831-004) means "your input" and neutral means "the
    // market": the same numbers can carry opposite meanings, and both must be able to hold at once
    // — Calls frozen on 24-Jul while Puts reads 24-Jul live. One shared record could not express
    // that, which is why the split exists.
    //
    // Kept OUT of WorkingInputs/TabInputs deliberately — those are cloned on Run, and
    // isDualSideInputs() discriminates on a `calls` member, so a map living there would break the
    // discriminator.
    readingsByDate: MarketConditionOverrides = {};

    overridesByTab: {
        calls: MarketConditionOverrides;
        puts: MarketConditionOverrides;
        callsAndPuts: MarketConditionOverrides;
    } = {calls: {}, puts: {}, callsAndPuts: {}};

    // NOTE: there is deliberately NO enable/disable flag (owner ruling 2026-07-22 — the block is
    // always visible, never collapsed). The master toggle it replaced was redundant with `edited`:
    // a seeded-but-untouched date never reaches the wire (D6/D7), so with nothing edited a run is
    // already byte-identical to pre-CR behaviour. The per-date dirty flag IS the switch.
    //
    // Which date of the range the fieldset is currently showing. ALL_DATES_INDEX (-1) is the
    // AGGREGATE position: an edit made there applies to EVERY date in the range at once.
    //
    // REPLACES D3's "the first date of a range loads first" (client screencast 2026-09-17, card
    // nTGh4JWd — "when it preloads start with all and then you can move to the individual dates").
    // D3's ARROW rule survives untouched; only its default position is gone. The old behaviour is
    // not a fallback — there is no path back to it.
    //
    // 0 when the selection resolves to a single date or none: with one date there is nothing to
    // standardise across, so the aggregate position would be a second name for that same date
    // (owner ruling, same card).
    overrideDateIndex = ALL_DATES_INDEX;

    // Post-Run the tab's questionnaire collapses to the breadcrumb bar (frame 03); the breadcrumb's
    // expandRequested re-opens it. Per tab — a collapse on one tab never touches another.
    questionnaireCollapsed: { calls: boolean; puts: boolean; callsAndPuts: boolean } =
        {calls: false, puts: false, callsAndPuts: false};

    // Calls & Puts wide tab: either side collapses to a click-to-expand rail while working the
    // other (§5, frame 06). VISUAL only — a collapsed side still gates the Run (isTabComplete).
    sideCollapsed: { calls: boolean; puts: boolean } = {calls: false, puts: false};

    // WI-16/A8: the RESULTS columns collapse on their own state, INDEPENDENT of the questionnaire
    // above (owner 2026-07-14 — repeals the WI-12/§6 "lockstep, both ways" rule). Independence is
    // what makes the results collapsible at all: post-Run the questionnaire is hidden behind the
    // breadcrumb bar, so its header — the only collapse control WI-12 built — is off-screen exactly
    // when the user is reading results. The results header is now its own control (results-list).
    // Reset on a fresh conversation AND on every Run: results the user just asked for must be visible.
    resultsSideCollapsed: { calls: boolean; puts: boolean } = {calls: false, puts: false};

    // ---- parameter-change-vs-run cues (CR-20260831-004) ------------------------------------
    //
    // Per-tab flat maps of the questionnaire fields (and override boxes) that no longer match what
    // the tab's CURRENT run sent — see opg-copilot-param-diff.ts for the key grammar. Empty while
    // the tab has never run (no baseline => nothing can be "changed since the run") and re-emptied
    // by a new Run or a conversation reset. Recomputed as FRESH objects in recomputeParamChanges()
    // so the OnPush children wake on the reference change.
    paramChanges: { calls: ParamChangeMap; puts: ParamChangeMap; callsAndPuts: ParamChangeMap } =
        {calls: {}, puts: {}, callsAndPuts: {}};

    // The side questionnaires' pre-sliced views of the maps above (prefix stripped): single-side
    // tabs read 'side.*'; the dual tab's two instances read 'calls.*' / 'puts.*'.
    sideParamChanges: { calls: ParamChangeMap; puts: ParamChangeMap; duoCalls: ParamChangeMap; duoPuts: ParamChangeMap } =
        {calls: {}, puts: {}, duoCalls: {}, duoPuts: {}};

    // (Round 4: the date block's cues are computed IN the block from [baseline] — see
    // dateBaseline() below. The diff module's 'date.*' keys still feed the breadcrumb chip and
    // the stale union, but not the block's visuals: an incomplete selection reaches the model as
    // null and loses its mode, which mis-attributed a mode switch to the value boxes.)

    // The ANCHORED override date's changed boxes on the ACTIVE tab (null = none). The override
    // fieldset renders one date at a time, so this is resolved here rather than in the child.
    currentOverrideChanged: OverrideChangedKeys | null = null;

    // What the current run SENT as overrides, per tab — deep-copied at Run fire (the questionnaire
    // baseline already lives in Run.sentInputs; overrides are deliberately outside TabInputs, so
    // they need their own snapshot). null = that tab has no current run baseline.
    private _sentOverrides: { calls: MarketConditionOverrides | null; puts: MarketConditionOverrides | null; callsAndPuts: MarketConditionOverrides | null } =
        {calls: null, puts: null, callsAndPuts: null};

    // Height lock for the two-column questionnaire section (WI-12, owner review). The .duo row
    // is sized by its tallest child: with ONE side collapsed the rail stretches to the expanded
    // side's questionnaire, but collapsing the SECOND side leaves only two rails, so the section
    // would drop to the rail's min-height and the whole dialog visibly shrinks. The expanded
    // markup is *ngIf'd out, so CSS has nothing left to size against — we capture the height
    // while the last expanded side is STILL laid out (the input flip happens after) and pin it
    // as a min-height until a side re-expands.
    duoLockedHeightPx: number | null = null;

    @ViewChild('duoSection') private _duoSection: ElementRef<HTMLElement>;

    // ---- run backstop (§11 stuck ≠ empty). RUN-SCOPED like the SingleFlightLock: a stale
    // run's late continuation must never clear the CURRENT run's timer (a second stall would
    // then hang the lock forever with no stuck-detection). ----
    private _backstopHandle: any = null;
    private _backstopRunId: string | null = null;

    // ---- memo caches so template bindings keep stable references across CD passes ----
    private _expKeysCache: string[] = [];
    private _ladderCache: number[] = [];

    // ---- stale-grid indicator (WI-15 / A6) -------------------------------------------------
    // The grid state the CoPilot last SYNCED WITH, captured at the two moments it actually reads
    // the panel: conversation start (first open / refresh) and Run fire. Yellow = the live grid no
    // longer matches this. null = no conversation yet => nothing can be stale (donor rule: a null
    // baseline is never stale, so the FAB cannot flash before the CoPilot has ever been opened).
    private _capturedGrid: OpgGridFingerprint | null = null;
    // The loaded-date button/chip label as it read AT CAPTURE (with its DTE badge). Frozen text,
    // not a re-derivation: while the expiration has drifted the control must show what the CoPilot
    // captured, not silently repaint to the value that moved out from under it.
    private _capturedLoadedDateText: string | null = null;
    private _lastEmittedStale = false;
    private _lastEmittedGridStale = false;
    private _lastLoggedDrift = '';

    private _destroyed = false;

    // ---- dynamic popup height (APG donor recipe): one ResizeObserver on the scrollable content
    // column emits the natural content height; the host owns the clamp + the popup geometry. ----
    private _scrollContentEl: HTMLElement | null = null;
    private _contentResizeObserver: ResizeObserver | null = null;
    private _contentHeightRafId: number | null = null;
    private _lastEmittedContentHeight = -1;

    // Re-attaches the observer whenever the scroll-content element appears/disappears (it lives
    // under *ngIf="contentVisible", which toggles on new-conversation).
    @ViewChild('scrollContent')
    set scrollContentRef(ref: ElementRef<HTMLElement> | undefined) {
        const el = ref ? ref.nativeElement : null;
        if (el === this._scrollContentEl) {
            return;
        }
        if (this._contentResizeObserver && this._scrollContentEl) {
            this._contentResizeObserver.unobserve(this._scrollContentEl);
        }
        this._scrollContentEl = el;
        if (el && this._contentResizeObserver) {
            this._contentResizeObserver.observe(el);
            this._scheduleContentMeasure();
        }
    }

    // Warned clear-on-run confirm (WI-5). The shared platform confirm primitive, OWNED by this
    // orchestrator (a UI concern — deliberately kept OFF the data/mutation facade). Always mounted
    // in the template (outside *ngIf="contentVisible"), so it resolves before the first Run and
    // survives a fresh-conversation toggle. show() resolves on Yes / rejects on No (its contract).
    @ViewChild(GenericConfirmationDialogComponent) private _clearOnRunConfirm: GenericConfirmationDialogComponent;

    // ONE stable bound function passed down to the results list, so the OnPush child's input never
    // churns. The loaded grid cell's price wins for a previewed trade (grid = tradeable reference,
    // §9); null while unpriced / not loaded.
    readonly loadedPriceOf = (suggestionId: string): number | null => {
        const row = this.session.findLoadedBySuggestionId(suggestionId);
        if (!row || !this.root) {
            return null;
        }
        return this.root.readLoadedTradePrice(row.side, row.strategyId, row.startingStrike);
    };

    // ---- Angular lifecycle -------------------------------------------------------------------

    ngOnInit(): void {
        // CR-20260727-001. App Settings can turn Expected Move off (or clear the Custom Percent)
        // while a questionnaire already holds an Expected Range selection. The controls vanish on
        // the next render, but the MODEL would keep the now-unofferable selection and the Run gate
        // would still pass it — so the selection is sanitized here, at the one place that owns
        // workingInputs.
        // Seed BEFORE subscribing: the bus only reports CHANGES, so without this the dialog would
        // open assuming the setting is on and stay wrong until the user next saved App Settings.
        this.expectedRangeAvailable = this._expectedMoveService.isExpectedRangeUsableForCoPilot();

        this._appSettingsSub = this._messageBus.of(AppSettingsUpdatedMessageTopic)
            .subscribe(() => this.onAppSettingsUpdated());
    }

    ngAfterViewInit(): void {
        if (typeof ResizeObserver === 'undefined') {
            return;   // older browsers: popup keeps its bound height, no auto-size (graceful degrade)
        }
        this._contentResizeObserver = new ResizeObserver(() => this._scheduleContentMeasure());
        if (this._scrollContentEl) {
            this._contentResizeObserver.observe(this._scrollContentEl);
            this._scheduleContentMeasure();
        }
    }

    ngOnDestroy(): void {
        this._destroyed = true;
        this.stopLiveFeed();   // releases the vertical-skew slot — see stopLiveFeed
        this.clearBackstop();
        if (this._appSettingsSub) {
            this._appSettingsSub.unsubscribe();
            this._appSettingsSub = null;
        }
        if (this._contentHeightRafId !== null) {
            cancelAnimationFrame(this._contentHeightRafId);
            this._contentHeightRafId = null;
        }
        if (this._contentResizeObserver) {
            this._contentResizeObserver.disconnect();
            this._contentResizeObserver = null;
        }
    }

    // ---- Expected Move settings (CR-20260727-001) ----------------------------------------------

    private _appSettingsSub: Subscription | null = null;

    /**
     * Can the user's Expected Move setting currently produce a range (owner rulings D2/D3)?
     *
     * A FIELD, not a getter that reads the service. The questionnaires are OnPush children and a
     * settings save is not an event in their subtree, so a getter was only re-evaluated when
     * something ELSE happened to check them — which is why the gate visibly lagged until the user
     * clicked the pill (owner report 2026-07-27 R1: "it kind of awaits for a refresh"). Refreshing
     * a field here and binding it down gives the children a reference change to wake on.
     */
    expectedRangeAvailable = true;

    /**
     * App Settings were saved. Re-read availability so it flows down to the questionnaires.
     *
     * NO EARLY RETURN. The first cut bailed out when the setting was usable, so ENABLING Expected
     * Move updated nothing at all and the controls stayed greyed until an unrelated redraw. The
     * handler has to run in BOTH directions — that asymmetry was half the reported defect.
     *
     * It deliberately does NOT touch workingInputs. Owner ruling 2026-07-27 R2: a selected Expected
     * Range STAYS selected when the setting goes off ("I do not need to switch to this one"). The
     * questionnaire simply becomes unrunnable — `hasUnusableExpectedRange` enforces that on the Run
     * gate, the disabled pill is the on-screen cue, and re-enabling the setting restores a working
     * questionnaire with the user's start strike untouched.
     *
     * Fires on EVERY app-settings save (the bus carries no payload); recomputing one boolean is
     * cheap enough that filtering would cost more than it saves.
     */
    @DetectMethodChanges()
    private onAppSettingsUpdated(): void {
        this.expectedRangeAvailable = this._expectedMoveService.isExpectedRangeUsableForCoPilot();
    }

    /**
     * Does the tab about to be Run hold an Expected Range selection the settings can no longer
     * satisfy? Scoped to the ACTIVE tab, matching canRun's own scope — a collapsed tab's stale
     * selection blocks that tab when the user switches to it, not this one.
     *
     * PRIMARY enforcement since R2, not a backstop. The selection is now deliberately left in place
     * when the setting goes off, so this getter is the only thing between an unsatisfiable
     * questionnaire and a Run the backend would reject.
     */
    get hasUnusableExpectedRange(): boolean {
        if (this.expectedRangeAvailable) {
            return false;
        }

        const inputs = this.workingInputs[this.activeTab];
        return isDualSideInputs(inputs)
            ? sideUsesExpectedRange(inputs.calls) || sideUsesExpectedRange(inputs.puts)
            : sideUsesExpectedRange(inputs.side);
    }

    // ---- host entry points (contract) ----------------------------------------------------------

    // Called by the host when the popup opens. A6 (owner, 2026-07-14): the window CLOSE is a
    // MINIMIZE — "OPG Copilot must keep its state and data until reset. Minimizing and restoring
    // copilot must not affect entered data." So an open on an EXISTING conversation just re-syncs the
    // view (dx-popup + OnPush can leave the reopened DOM stale — APG donor's refreshView) and
    // re-checks the stale indicator against whatever the user did to the grid while it was shut.
    // A fresh conversation starts only when there is none — or when the symbol drifted anyway, which
    // the §8 hook should already have handled (belt-and-braces: this component is mounted for the
    // panel's whole life, so a missed hook must not leave a conversation pointing at the wrong chain).
    // The session ends at the refresh button (WI-10) or a symbol change — never at a close.
    public onOpened(): void {
        const symbol = this.readSymbolSafe();
        const conversation = this.session.conversation;
        if (!conversation || conversation.symbol !== symbol) {
            this.startFreshConversation(symbol);
        } else {
            this.onGridStateMaybeChanged();   // the grid may have moved while we were minimized
        }
        this.widthModeChange.emit(this.widthModeFor(this.activeTab));
        this._changeDetector.detectChanges();
        this._scheduleContentMeasure();
    }

    // The panel's underlying changed — the chain changed under the cards (§8). The host has already
    // cleared all borders; drop everything and start afresh against the new symbol.
    //
    // A symbol change RESETS the conversation, exactly as it always has (owner ruling 2026-07-14, on
    // re-asking — it was never overruled by A6, whose persistent-session rule is about MINIMIZING the
    // window, not about changing the underlying). This is also what keeps the CoPilot honest: a trade
    // computed for SPX cannot be priced, previewed or even sensibly displayed on an SPY ladder, and the
    // fresh conversation reads the NEW symbol's chain (the host calls this AFTER tradingInstrument and
    // availableExpirations are reassigned). The new baseline is captured by startFreshConversation and
    // settles as the panel finishes restoring (adoptPreLoadBaseline).
    public onSymbolChanged(): void {
        this.clearBackstop();
        this.session.endConversation();
        this.startFreshConversation(this.readSymbolSafe());
    }

    // The grid MAY have changed (host hook, WI-15): the panel calls this from every USER edit path
    // that touches a fingerprint member — expiration, chosen ATM, default qty, symbol. The CoPilot
    // is OnPush and the change originates in the GRID, so nothing here would re-render on its own:
    // the decorator's CD pass is what re-evaluates the yellow. Cheap (four compares), safe to
    // over-call, and a no-op before the first conversation exists.
    //
    // NOT trippers (they must never reach this hook, or must compare equal when they do): the
    // market/dynamic ATM, quote ticks, chain reloads — and every CoPilot-OWN write (the WI-6
    // preview navigation re-baselines its own expiration member the instant it lands, so by the
    // time this hook runs behind it the live value already matches the baseline).
    @DetectMethodChanges()
    public onGridStateMaybeChanged(): void {
        // CR-20260722-002: the override block is always visible, so it must be filled whenever the
        // panel settles — on open, and after the panel moves. Idempotent, and deliberately BEFORE
        // the baseline guard below: that guard returns early when there is no conversation, but the
        // block still renders and would sit empty.
        this.refreshLiveFeed();

        if (!this._capturedGrid) {
            return;   // no conversation => no baseline => nothing can be stale
        }
        const live = this.liveGridFingerprint;

        // Startup guard (donor's adoptEmptyBaselineIfNeeded): a baseline taken before the panel had a
        // grid at all (no symbol / no chain yet) adopts the panel's first real reads instead of
        // reporting them as a user edit — that is the yellow flash at first open. It does NOT adopt
        // per null member: once the panel is loaded, a null Default Qty is a genuine reading, and
        // adopting it here would silently swallow the user's very first edit of it (this hook is the
        // one the edit arrives on).
        const adopted = adoptPreLoadBaseline(this._capturedGrid, live);
        if (adopted !== this._capturedGrid) {
            this._capturedGrid = adopted;
            this._capturedLoadedDateText = this.liveLoadedDateText;
        }

        this.logDriftTransition(driftedGridMembers(this._capturedGrid, live), live);
        this._emitStale();
    }

    // Consistency rule (§8): if a CoPilot-loaded strategy leaves the grid by ANY means, the card's
    // Previewed state follows it out (the host has already dropped the border).
    @DetectMethodChanges()
    public onHostStrategyRemoved(strategyId: string): void {
        const row = this.session.unregisterByStrategyId(strategyId);
        if (!row) {
            return;
        }
        const suggestion = this.session.findSuggestion(row.suggestionId);
        if (suggestion) {
            suggestion.state.previewed = false;
        }
    }

    // ---- derived view state ----------------------------------------------------------------------

    tabOf(kind: TabKind): Tab | null {
        return this.session.tab(kind);
    }

    // Visible-filtered expiration keys ('yyyy-MM-dd') for the date blocks. Memoized so the OnPush
    // children see a stable array reference while the chain is unchanged.
    get availableExpirationKeys(): string[] {
        // CR-20260924-002: a host in ES mode offers the ES chain's dates here while the strike
        // pickers keep the SPX chain (readAvailableExpirations). Absent member / null = as before.
        const dated = this.root && this.root.readDateExpirations ? this.root.readDateExpirations() : null;
        const source = dated ? dated : (this.root ? (this.root.readAvailableExpirations() || []) : []);
        const keys: string[] = [];
        for (let i = 0; i < source.length; i++) {
            keys.push(source[i].optionExpirationDate);
        }
        if (!sameList(keys, this._expKeysCache)) {
            this._expKeysCache = keys;
        }
        return this._expKeysCache;
    }

    // The grid rows' strikes — the SKILL CONTEXT ladder ("what the trader sees", §6).
    // NOT the picker/validation list (that is pickerStrikes below). Memoized like the keys.
    get strikeLadder(): number[] {
        const source = this.root ? (this.root.readStrikeLadder() || []) : [];
        if (!sameList(source, this._ladderCache)) {
            this._ladderCache = source.slice();
        }
        return this._ladderCache;
    }

    // The strike pickers' expiration: the NEAREST available (visible) expiration,
    // DATE-INDEPENDENT of the questionnaire's chosen date(s) — the HedgeMatrix copilot's
    // owner rule 2026-06-21, mirrored here at owner smoke 2026-07-10. Min by the
    // 'yyyy-MM-dd' key (string compare is date order); the returned descriptor reference
    // is stable while the chain is unchanged, so OnPush inputs stay quiet.
    get nearestExpiration(): OptionExpirationDescriptor | null {
        const source = this.root ? (this.root.readAvailableExpirations() || []) : [];
        let nearest: OptionExpirationDescriptor | null = null;
        for (let i = 0; i < source.length; i++) {
            if (!nearest || source[i].optionExpirationDate < nearest.optionExpirationDate) {
                nearest = source[i];
            }
        }
        return nearest;
    }

    // The list the strike pickers offer AND the 'strikes'-mode membership validation runs
    // against — the SAME list by construction (picker-validator parity, A3): a strike the
    // dropdown can produce always validates, and Run can never gate on an unpickable value.
    get pickerStrikes(): number[] {
        const nearest = this.nearestExpiration;
        return (nearest && nearest.strikes) ? nearest.strikes : EMPTY_LADDER;
    }

    get targetInstrument(): TradingInstrument | null {
        return this.root ? this.root.readTradingInstrument() : null;
    }

    // The Chosen ATM strike VALUE shown on the "Chosen ATM · 7500" pill (WI-4) — the DISPLAY value:
    // the CAPTURED strike while the chosen ATM has drifted (WI-15/A6 — the pill goes yellow and
    // holds what the CoPilot captured; repainting it to the live value would erase the very
    // divergence the indicator exists to show), the live strike otherwise. Null → bare label.
    // Market ATM shows no value (owner 2026-07-13), so it is not read here. Run is UNAFFECTED: it
    // reads the facade live at fire time (buildSkillRequest).
    get chosenAtmValue(): number | null {
        if (this.isChosenAtmStale) {
            return this._capturedGrid.chosenAtm;
        }
        return this.liveChosenAtmValue;
    }

    get presetUnderlying(): string {
        return this.root && this.root.readEsMode && this.root.readEsMode() ? 'ES'
            : this.root ? this.root.readSymbol() : null;
    }
    presetApgSource(side: 'calls' | 'puts'): string | null {
        return this.root && this.root.readPresetApgSource ? this.root.readPresetApgSource(side) : null;
    }

    private get liveChosenAtmValue(): number | null {
        if (!this.root) {
            return null;
        }
        const v = this.root.readChosenAtm();
        return (typeof v === 'number' && isFinite(v)) ? v : null;
    }

    // The panel's currently-loaded expiration as a Date (WI-3), or null when the panel has no
    // selected expiration yet (chain still loading). Re-read LIVE from the facade on every access —
    // the 'loaded' date must resolve at Run time, never freeze at dialog open. Parsed by the local
    // key->Date helper (kept in-file, not a shared export — the sibling stays self-contained).
    get loadedExpirationDate(): Date | null {
        return this.root ? parseExpirationDayKey(this.root.readSelectedExpirationDate()) : null;
    }

    // CR-20260723-001: the expiration the CoPilot CAPTURED at its last capture point (open / refresh).
    // The Co-Pilot is self-contained: what the override indicators SHOW and what a Run SENDS both anchor
    // to THIS date, never a live read of the OPG panel. Changing the panel expiration afterward only
    // lights the yellow drift cue; only a refresh re-captures. null before the first conversation.
    private get capturedLoadedExpirationKey(): string | null {
        return this._capturedGrid ? this._capturedGrid.expiration : null;
    }

    private get capturedLoadedExpirationDate(): Date | null {
        const key = this.capturedLoadedExpirationKey;
        return key ? parseExpirationDayKey(key) : null;
    }

    // The visible expiration descriptor matching the panel's selected expiration, or null (the
    // selected date is not in the current chain — e.g. mid symbol-switch). Sole use: the loaded
    // label's days-to-expiration badge (mirrors the trade card's findExpirationDescriptor).
    private get loadedExpirationDescriptor(): OptionExpirationDescriptor | null {
        if (!this.root) {
            return null;
        }
        const key = this.root.readSelectedExpirationDate();
        if (!key) {
            return null;
        }
        const list = this.root.readAvailableExpirations() || [];
        for (let i = 0; i < list.length; i++) {
            if (list[i].optionExpirationDate === key) {
                return list[i];
            }
        }
        return null;
    }

    // The loaded-date button label + the breadcrumb 'loaded' chip text — the DISPLAY value: the
    // CAPTURED label while the panel's expiration has drifted (WI-15/A6 — owner demo: the panel
    // moved Tue→Wed while the button kept saying Tue, in yellow), the live label otherwise. The
    // Run is UNAFFECTED — 'loaded' still resolves against the panel's CURRENT expiration at fire
    // time (resolveDateForRequest reads loadedExpirationDate, never this text), and firing
    // re-captures the baseline, so the button snaps back to live as the run goes out.
    get loadedDateText(): string | null {
        if (this.isExpirationStale) {
            return this._capturedLoadedDateText;
        }
        return this.liveLoadedDateText;
    }

    // The panel's CURRENT loaded expiration in the family format WITH the days-to-expiration badge
    // ("Tue 14-Jul-26 (3d)"). The (Nd) part reads descriptor.daysToExpiration — the backend's own
    // DTE, EXACTLY as the trade card renders it (family parity) — and is dropped if the date is not
    // in the current chain. Null when there is no selected expiration (button shows the placeholder,
    // the 'loaded' selection is Run-gated). Same string value per date, so handing it to the OnPush
    // date-block / breadcrumb children costs no churn while the panel date holds.
    private get liveLoadedDateText(): string | null {
        const d = this.loadedExpirationDate;
        if (!d) {
            return null;
        }
        const desc = this.loadedExpirationDescriptor;
        const dte = desc && typeof desc.daysToExpiration === 'number' && isFinite(desc.daysToExpiration)
            ? ' (' + desc.daysToExpiration + 'd)'
            : '';
        return formatDowDate(d) + dte;
    }

    // CR-20260901-001: the portfolio's default quantity for the questionnaires' mode-flip value
    // conversion — a live facade read per CD (cheap; same idiom as chosenAtmValue's feed).
    get defaultQty(): number | null {
        const dq = this.root ? this.root.readDefaultQty() : null;
        return typeof dq === 'number' && isFinite(dq) ? dq : null;
    }

    // ---- stale-grid indicator: the derived view state (WI-15/A6) --------------------------------

    // The panel's fingerprint reads, RIGHT NOW.
    private get liveGridFingerprint(): OpgGridFingerprint {
        if (!this.root) {
            return makeGridFingerprint(null, null, null);
        }
        return makeGridFingerprint(
            this.root.readSelectedExpirationDate(),
            this.root.readChosenAtm(),
            this.root.readDefaultQty()
        );
    }

    // Which captured members the live grid has moved away from — MINUS the ones the CoPilot would not
    // act on anyway (relevantGridMembers). PURE: the baseline is never touched here (upkeep belongs to
    // the capture points + onGridStateMaybeChanged's adoption); a self-healing getter would clear the
    // very drift it is meant to report. Recomputed per CD pass — three compares, and the booleans
    // below (not this array) are what the template binds, so no OnPush input ever churns on it.
    get staleGridMembers(): OpgGridMember[] {
        const drifted = driftedGridMembers(this._capturedGrid, this.liveGridFingerprint);
        return relevantGridMembers(drifted, this.isChosenAtmInUse);
    }

    // Does any questionnaire in this conversation actually READ the panel's chosen ATM? Only the ATM
    // Offset price-range form does (it anchors its region on it). Owner ruling 2026-07-14: a moved
    // centre strike must not light the yellow unless a side is on ATM Offset — the other two forms
    // name their strikes outright, so the panel's ATM is nothing their answers depend on.
    // Checked across ALL sides of all three tabs, not just the active one: the dialog is one
    // conversation, and a collapsed tab's questionnaire is still going to be Run.
    get isChosenAtmInUse(): boolean {
        const w = this.workingInputs;
        return sideUsesAtmOffset(w.calls.side)
            || sideUsesAtmOffset(w.puts.side)
            || sideUsesAtmOffset(w.callsAndPuts.calls)
            || sideUsesAtmOffset(w.callsAndPuts.puts);
    }

    // ANY member drifted → the refresh button (and the collapsed FAB) go yellow.
    get isGridStale(): boolean {
        return this.staleGridMembers.length > 0;
    }

    // Per-control cues: expiration drift lights the loaded-date button + the 'loaded' breadcrumb
    // chip; chosen-ATM drift lights the Chosen ATM pill. Default-qty and symbol drift have no
    // dedicated control in the dialog — they light the refresh button / FAB only (owner ruling:
    // include them in the fingerprint, "include default qty and underlying change as well").
    get isExpirationStale(): boolean {
        return this.staleGridMembers.indexOf('expiration') >= 0;
    }

    get isChosenAtmStale(): boolean {
        return this.staleGridMembers.indexOf('chosenAtm') >= 0;
    }

    // ONE Run button per tab (§5): tab structurally complete AND no run in flight (global
    // single-flight). Presence/sanity gating only — the skill is the only judge (courier, §6).
    // Strike membership validates against pickerStrikes (the pickers' own list, A3).
    get canRun(): boolean {
        if (!this.root || this.session.isRunInFlight) {
            return false;
        }
        // D22: the override block is part of "structurally complete" too. It is NOT in
        // workingInputs (see the readings/overrides fields — kept out so it cannot break
        // isDualSideInputs), and isTabComplete only ever inspects workingInputs, so the block was
        // invisible to this gate from the day it was built. That is why a half-filled override
        // could start a run: the record was then discarded at the wire and the engine measured the
        // market, reporting success over input the user had supplied and never used.
        if (this.hasInvalidOverrides) {
            return false;
        }
        // CR-20260727-001. Since owner ruling R2 a selected Expected Range is LEFT in place when
        // the setting goes off — nothing rewrites it — so this is the sole thing that stops an
        // unsatisfiable questionnaire from being Run. The greyed pill is the cue; this is the gate.
        if (this.hasUnusableExpectedRange) {
            return false;
        }
        // CR-20260723-001: gate on the CAPTURED expiration (self-contained), matching what a Run sends.
        return isTabComplete(this.activeTab, this.workingInputs[this.activeTab], this.pickerStrikes, this.capturedLoadedExpirationDate);
    }

    // ---- tab + questionnaire handlers -------------------------------------------------------------

    @DetectMethodChanges()
    onActiveTabChange(tab: TabKind): void {
        if (!tab || tab === this.activeTab) {
            return;
        }
        this.activeTab = tab;
        // 'wide' ONLY for Calls & Puts (§5) — the host resizes the popup.
        this.widthModeChange.emit(this.widthModeFor(tab));
        // Each tab carries its OWN date selection, so switching tabs changes which dates the
        // override block covers just as surely as editing the date does.
        this.reanchorOverrides();
        // CR-20260831-004: currentOverrideChanged is resolved against the ACTIVE tab's anchor.
        this.recomputeParamChanges();
    }

    @DetectMethodChanges()
    onDateChange(tab: TabKind, date: OpgDateSelection): void {
        this.workingInputs[tab].date = date;
        // CR-20260722-002: the override block's dates are DERIVED from this selection, so changing
        // it re-anchors the block. Without this the fieldset appears the moment a range completes
        // but shows an empty row and never fetches — the seed was only ever triggered by the
        // toggle and the arrows, neither of which fires when the date changes (smoke 2026-07-22).
        if (tab === this.activeTab) {
            // Prune BEFORE re-anchoring, and only here — this is the one input that changes which
            // dates are covered. It stays out of reanchorOverrides (which also runs on a tab
            // switch) because a tab switch changes nothing about what any tab covers.
            // The old hazard behind this placement — pruning on a tab switch destroying the other
            // tab's records — is gone since D20: each map is pruned against its OWN tab's dates.
            this.pruneOverridesOutsideRange();
            this.reanchorOverrides();
        }
        // CR-20260831-004: the date IS a parameter, and the prune above may have dropped override
        // records too — re-diff after both settle.
        this.recomputeParamChanges();
    }

    // A side questionnaire mutated its parent-owned SideInputs IN PLACE and emitted `changed`
    // (void — the child's contract). Nothing to copy: this handler exists so the OnPush
    // orchestrator re-evaluates canRun (and any other derived state) after the mutation.
    @DetectMethodChanges()
    onSideInputsChanged(): void {
        // WI-15: staleness can flip HERE, with no grid change at all — switching a side's Price Range
        // to (or off) ATM Offset changes whether a drifted chosen ATM is relevant. The in-dialog
        // surfaces re-read it through the getters on this CD pass, but the launcher's cue is pushed,
        // not pulled: without this the FAB would disagree with the refresh button. Deduped, so a
        // questionnaire edit that changes nothing emits nothing.
        // CR-20260831-004: the same edit may also flip the param-vs-run cues; the recompute ends
        // with _emitStale(), so the WI-15 push above is preserved inside it.
        this.recomputeParamChanges();
    }

    @DetectMethodChanges()
    onSideCollapsedChange(side: OpgSide, collapsed: boolean): void {
        const other: OpgSide = side === 'calls' ? 'puts' : 'calls';
        const bothCollapsedAfter = collapsed && this.sideCollapsed[other];

        // Measured BEFORE the flip: the DOM still shows the last expanded side, so this is the
        // height the section is about to lose. Any expand releases the lock (natural sizing).
        if (bothCollapsedAfter) {
            const measured = this._duoSection ? this._duoSection.nativeElement.offsetHeight : 0;
            if (measured > 0) {
                this.duoLockedHeightPx = measured;
            }
        } else {
            this.duoLockedHeightPx = null;
        }

        this.sideCollapsed[side] = collapsed;
    }

    // WI-16/A8: the results columns' own collapse — no height lock here. The results-list owns that
    // measurement itself (its @Input setters fire while its DOM still shows the pre-collapse layout;
    // the orchestrator cannot see the child's height at the right instant).
    @DetectMethodChanges()
    onResultsSideCollapsedChange(side: OpgSide, collapsed: boolean): void {
        this.resultsSideCollapsed[side] = collapsed;
    }

    @DetectMethodChanges()
    onExpandRequested(tab: TabKind): void {
        this.questionnaireCollapsed[tab] = false;
    }

    // Manual collapse — the user-driven inverse of onExpandRequested. Re-hides the questionnaire to
    // the breadcrumb summary WITHOUT running (no ESS request, no results/quote change): the collapse
    // in onRun is a run side-effect, not the only path back to the summary. Gated in the template by
    // hasCompletedRun so it never appears in the pristine (never-run) state.
    @DetectMethodChanges()
    onCollapseRequested(tab: TabKind): void {
        this.questionnaireCollapsed[tab] = true;
    }

    // "a run has completed on this tab" — gates the manual collapse hint (template *ngIf). A run
    // exists once onRun creates it; 'pending' is the in-flight window (the form is already collapsed
    // then, so it is moot); every settled outcome — including 'empty' ("No trades matched") — counts,
    // matching the ruling that collapse must be available even when the run produced zero trades.
    hasCompletedRun(tab: TabKind): boolean {
        const run = this.tabOf(tab)?.currentRun;   // tabOf is Tab | null — no tab ⇒ no completed run
        return !!run && run.outcome !== 'pending';
    }

    // Whole-dialog reset (WI-10/A6 — the refresh button after the tabs; the HedgeMatrix donor's
    // onNewConversation, duplicated): owner ruling 2026-07-14 — "it must reset the state of the
    // whole dialog, not just the tab it's currently selected."
    //   (1) Un-preview + clear every CoPilot-loaded trade (owner: "un-preview and clear them") —
    //       real unloads via the facade, borders included; user-built strategies untouched.
    //       Snapshot iteration (session.loadedTrades copies) — the walk never trips on removal.
    //   (2) Drop an in-flight run SILENTLY: force-clear the backstop; the conversation restart
    //       releases the single-flight lock, and a late skill result is discarded by
    //       completeRun's currency check (the run's tab lives in the dead conversation).
    //   (3) Fresh conversation on the CURRENT symbol: all three tabs back to their makeDefault*
    //       defaults, results/breadcrumb/collapse states cleared, questionnaires recreated via
    //       the family contentVisible toggle. The active tab is deliberately preserved (family
    //       ruling 2026-06-23 — never jump the user back to the first tab;
    //       startFreshConversation doesn't touch activeTab).
    // Deliberately ENABLED during a run (donor parity — the drop IS the cancel).
    @DetectMethodChanges()
    onNewConversation(): void {
        const rows = this.session.loadedTrades;   // snapshot copy
        if (this.root) {
            for (let i = 0; i < rows.length; i++) {
                this.root.unloadTrade(rows[i].side, rows[i].strategyId);
                this.root.clearBorder(rows[i].strategyId);
            }
        }
        const hadRunInFlight = this.session.isRunInFlight;
        this.clearBackstop();
        this.session.endConversation();
        this.startFreshConversation(this.readSymbolSafe());
        // §11 trail: what the reset dropped.
        console.log('[OpgCopilot] reset (new conversation) — un-previewed ' + rows.length
            + ' trade(s)' + (hadRunInFlight ? ', dropped an in-flight run' : ''));
    }

    // ---- Market-condition overrides (CR-20260722-002) ----------------------------------------

    // The dates the override fieldset can step through, for the ACTIVE tab's date selection.
    // 'loaded'/'single' => exactly one; 'range' => every LISTED expiration inside the range, which
    // is the same intersection the backend normalizer performs (dates outside the chain are not
    // run, so offering to override them would be a lie).
    get overrideDateKeys(): string[] {
        return this.dateKeysFor(this.workingInputs[this.activeTab].date);
    }

    // Every date any tab currently covers. Scope for the READINGS cache only — a reading is keyed
    // by date and shared, so it stays useful while any tab still covers that date. Overrides are
    // NOT pruned against this: they belong to one tab and are pruned against that tab's own dates
    // (D20).
    private get allTabsDateKeys(): string[] {
        const out: string[] = [];
        const tabs: TabKind[] = ['calls', 'puts', 'callsAndPuts'];
        for (let t = 0; t < tabs.length; t++) {
            const keys = this.dateKeysFor(this.workingInputs[tabs[t]].date);
            for (let i = 0; i < keys.length; i++) {
                if (out.indexOf(keys[i]) < 0) {
                    out.push(keys[i]);
                }
            }
        }
        return out;
    }

    private dateKeysFor(sel: OpgDateSelection): string[] {
        if (!sel) {
            return [];
        }
        if (sel.kind === 'loaded') {
            // CR-20260723-001: anchor to the CAPTURED expiration, not the panel's live one — the block
            // must not follow the OPG panel; a panel change only lights the yellow (self-contained).
            const d = this.capturedLoadedExpirationDate;
            return d ? [toDateKey(d)] : [];
        }
        if (sel.kind === 'single') {
            return sel.date instanceof Date ? [toDateKey(sel.date)] : [];
        }
        if (!(sel.start instanceof Date) || !(sel.end instanceof Date)) {
            return [];
        }
        const lo = toDateKey(sel.start);
        const hi = toDateKey(sel.end);
        // Ordinal string compare == chronological for 'yyyy-MM-dd' (the backend relies on the same
        // property in IntersectDates).
        return this.availableExpirationKeys.filter(k => k >= lo && k <= hi);
    }

    get currentOverrideDateKey(): string | null {
        const keys = this.overrideDateKeys;
        if (keys.length === 0) {
            return null;
        }
        const i = Math.min(Math.max(this.overrideDateIndex, 0), keys.length - 1);
        return keys[i];
    }

    // The block is always mounted, so it can be on screen before a date resolves — a half-picked
    // range, or a panel with no expiration loaded yet. Editable-looking boxes with nothing to write
    // to would be a lie (the row's own guard would silently drop the keystroke), so they are
    // disabled until there is a date to attach the values to.
    get overrideInputsDisabled(): boolean {
        return this.session.isRunInFlight || !this.currentOverrideDateKey;
    }

    // The ACTIVE tab's override map. A tab is an independent request (D20).
    private get tabOverrides(): MarketConditionOverrides {
        return this.overridesByTab[this.activeTab];
    }

    // What the anchored date's boxes show: this tab's override if it has one, otherwise the live
    // reading. Reference-stable per state — an override is replaced only when the user edits, a
    // reading only when a tick lands — so the OnPush child re-renders exactly when something moved.
    get currentOverride(): MarketConditionOverride | null {
        const key = this.currentOverrideDateKey;
        if (!key) {
            return null;
        }
        return this.tabOverrides[key] || this.readingsByDate[key] || null;
    }

    // Frame + legend cue (owner ruling D21): does ANY date this tab's block can step through carry
    // an override on THIS tab? Answers "I may have edited another date in the range and forgotten",
    // which the per-date colouring cannot show once you step off that date.
    //
    // Incomplete edits count. The frame reports that the user HAS edits here, not that they are
    // runnable — an edited-but-incomplete date is exactly the one worth being reminded about.
    get rangeOverridden(): boolean {
        const keys = this.overrideDateKeys;
        const held = this.tabOverrides;
        for (let i = 0; i < keys.length; i++) {
            if (held[keys[i]]) {
                return true;
            }
        }
        return false;
    }

    // Any date in this tab's range whose override is edited but NOT fully supplied (D22). Blocks
    // the Run — see canRun.
    //
    // Scoped to overrideDateKeys, not to the whole map, because only those dates reach the wire:
    // a leftover record outside the current range is pruned rather than sent, so blocking on it
    // would refuse a run over a value that cannot affect it.
    get hasInvalidOverrides(): boolean {
        const keys = this.overrideDateKeys;
        const held = this.tabOverrides;
        // Completeness is scoped to what THIS tab's run consumes (the 2026-08-04 ruling):
        // a calls tab requires the call-side skew only, puts the put-side, Calls & Puts both.
        const inputs = this.workingInputs[this.activeTab];
        const calls = isDualSideInputs(inputs) ? inputs.calls : this.activeTab === 'calls' ? inputs.side : null;
        const puts = isDualSideInputs(inputs) ? inputs.puts : this.activeTab === 'puts' ? inputs.side : null;
        const needsCalls = !!calls && calls.mode !== 'preset';
        const needsPuts = !!puts && puts.mode !== 'preset';
        if (!needsCalls && !needsPuts) { return false; }
        for (let i = 0; i < keys.length; i++) {
            const record = held[keys[i]];
            if (record && !isOverrideComplete(record, needsCalls, needsPuts)) {
                return true;
            }
        }
        return false;
    }

    // Family date format WITH the DTE badge — "Thu 23-Jul-26 (1d)" — identical to the date-kind
    // button and the breadcrumb. A user-facing surface never shows the raw 'yyyy-MM-dd' chain key;
    // that string is a WIRE identifier that happens to be human-readable, which is exactly what
    // makes leaking it easy to miss (smoke 2026-07-22).
    // The AGGREGATE position. Requires a real range: with one date (or none) there is nothing to
    // standardise across, so the position does not exist and every write falls back to that date.
    get isAllDatesAnchor(): boolean {
        return this.overrideDateIndex <= ALL_DATES_INDEX && this.overrideDateKeys.length > 1;
    }

    get overrideDateLabel(): string | null {
        if (this.isAllDatesAnchor) {
            return 'All Dates';
        }
        return this.dateLabelFor(this.currentOverrideDateKey);
    }

    // PROVENANCE (owner ruling 2026-09-17). Names the date the displayed numbers actually came
    // from, and is rendered ONLY on the All position — elsewhere the label already names it.
    //
    // Dropped only when every date in the range provably carries the same record: the numbers are
    // then true of all of them and there is no single source to attribute. Proof, never inference
    // — "the user probably edited" is exactly the assumption that would let a one-date reading
    // pass as an all-dates value. A partially-edited range therefore keeps the hint, which is the
    // case that motivated the ruling.
    get overrideSourceDateLabel(): string | null {
        if (!this.isAllDatesAnchor) {
            return null;
        }
        if (isUniformAcrossDates(this.tabOverrides, this.overrideDateKeys)) {
            return null;
        }
        return this.dateLabelFor(this.currentOverrideDateKey);
    }

    private dateLabelFor(key: string | null): string | null {
        if (!key) {
            return null;
        }
        const d = parseExpirationDayKey(key);
        if (!d) {
            return key;   // malformed key: show it rather than an empty label
        }
        return formatDowDate(d) + this.dteBadgeFor(key);
    }

    // ' (Nd)' from the chain descriptor's own daysToExpiration — the BACKEND's DTE, the same number
    // the expiration dropdown and the trade cards render. Empty when the date is not in the current
    // chain (never computed locally: a locally-derived DTE would silently disagree with every other
    // surface on holidays and half-days).
    private dteBadgeFor(key: string): string {
        const list = this.root ? this.root.readAvailableExpirations() : [];
        for (let i = 0; i < list.length; i++) {
            const desc = list[i];
            if (desc && desc.optionExpirationDate === key) {
                return (typeof desc.daysToExpiration === 'number' && isFinite(desc.daysToExpiration))
                    ? ' (' + desc.daysToExpiration + 'd)'
                    : '';
            }
        }
        return '';
    }

    // Arrows exist ONLY when a range is selected (D3) — and only when it spans more than one date.
    get showOverrideArrows(): boolean {
        return this.overrideDateKeys.length > 1;
    }

    // Stepping back off the first date lands on "All dates" rather than stopping there, so the
    // aggregate position is reachable again after the user has gone looking at individual dates.
    get canPrevOverrideDate(): boolean {
        return this.overrideDateIndex > ALL_DATES_INDEX;
    }

    get canNextOverrideDate(): boolean {
        return this.overrideDateIndex < this.overrideDateKeys.length - 1;
    }

    // Re-point the override block at the FIRST date of whatever is now selected and seed it
    // (owner ruling D3: "the first date of the range is always loaded first").
    //
    // Called from every input that can change WHICH dates the block covers: the date selection, the
    // tab switch, and dialog open. They are separate entry points to one rule, so the rule lives in
    // one place — the smoke defect was a missing caller, not wrong logic.
    //
    // Safe to call when nothing is selected yet: refreshLiveFeed starts nothing without a date, so
    // a half-picked range (start chosen, end not) costs nothing.
    private reanchorOverrides(): void {
        // Land on "All dates" whenever there is a range to standardise across, on the single date
        // otherwise. -1 still STREAMS the first date (the clamp in currentOverrideDateKey), so the
        // feed logic below is unchanged by the aggregate position.
        this.overrideDateIndex = this.overrideDateKeys.length > 1 ? ALL_DATES_INDEX : 0;
        this.refreshLiveFeed();
        // The anchor decides whether the changed-since-run cue is a union or one date's, so it is
        // re-resolved here as it is on the arrows. Callers vary in whether they also recompute.
        this.currentOverrideChanged = this.buildCurrentOverrideChanged();
    }

    // Drop every record whose date is no longer covered by the selection — INCLUDING edited ones
    // (owner ruling D13, 2026-07-22). A date that left the range cannot affect the run, so keeping
    // its values would be invisible state that silently reappears if the range is later widened
    // back over it.
    //
    // Deliberately a NO-OP while the selection resolves to no dates at all. Taken literally the
    // rule would wipe everything the moment a range goes incomplete — and clearing the end-date
    // picker to re-pick it does exactly that for one keystroke, which would destroy the whole
    // range's work as a side effect of editing it. "Falls out of the range" presupposes a range;
    // an unset range is not one.
    private pruneOverridesOutsideRange(): void {
        // Each tab's overrides are pruned against THAT TAB's own dates (D20). Narrowing the range
        // on Calls must not delete a record Puts still covers — the maps are per tab now, so the
        // union-across-tabs defence that used to protect them no longer applies here.
        const tabs: TabKind[] = ['calls', 'puts', 'callsAndPuts'];
        for (let t = 0; t < tabs.length; t++) {
            const keys = this.dateKeysFor(this.workingInputs[tabs[t]].date);
            if (keys.length === 0) {
                continue;   // this tab's selection resolves to nothing — see the no-op note above
            }
            this.pruneMap(this.overridesByTab[tabs[t]], keys);
        }

        // Readings are a cache of what the market said, keyed by date and shared, so one survives
        // while ANY tab still covers its date.
        const covered = this.allTabsDateKeys;
        if (covered.length > 0) {
            this.pruneMap(this.readingsByDate, covered);
        }
    }

    private pruneMap(map: MarketConditionOverrides, keep: string[]): void {
        const live: { [key: string]: boolean } = {};
        for (let i = 0; i < keep.length; i++) {
            live[keep[i]] = true;
        }
        const held = Object.keys(map);
        for (let i = 0; i < held.length; i++) {
            if (!live[held[i]]) {
                delete map[held[i]];
            }
        }
    }

    @DetectMethodChanges()
    onOverridePrev(): void {
        if (this.canPrevOverrideDate) {
            this.overrideDateIndex--;
            this.refreshLiveFeed();
            // CR-20260831-004: the anchored date moved — re-resolve which boxes are flagged.
            this.currentOverrideChanged = this.buildCurrentOverrideChanged();
        }
    }

    @DetectMethodChanges()
    onOverrideNext(): void {
        if (this.canNextOverrideDate) {
            this.overrideDateIndex++;
            this.refreshLiveFeed();
            // CR-20260831-004: the anchored date moved — re-resolve which boxes are flagged.
            this.currentOverrideChanged = this.buildCurrentOverrideChanged();
        }
    }

    // The row emits the whole record it wants for the anchored date; this installs it as an
    // override on THIS TAB (D20). The shared reading underneath is left alone and keeps ticking —
    // it has to, because another tab may be reading the same date live at this very moment.
    //
    // FREEZE (D17) is now shadowing rather than stopping: the user's record wins the display, so
    // their numbers stop moving even though the market's do not. Observably identical; it is the
    // only mechanism that lets Calls be frozen on 24-Jul while Puts reads 24-Jul live.
    // Which dates an edit or a clear writes to: EVERY date of the range on the aggregate position,
    // the anchored date otherwise. The one place the "All dates" promise is kept, so an edit and a
    // clear can never disagree about what "all" meant.
    private overrideWriteTargets(): string[] {
        if (this.isAllDatesAnchor) {
            return this.overrideDateKeys;
        }
        const key = this.currentOverrideDateKey;
        return key ? [key] : [];
    }

    @DetectMethodChanges()
    onOverrideChanged(next: MarketConditionOverride): void {
        if (!next) {
            return;
        }
        const targets = this.overrideWriteTargets();
        if (targets.length === 0) {
            return;
        }
        // A NEW object PER DATE, never a mutation and never a shared reference. In Calls & Puts
        // both fieldsets hold the same record and both are OnPush, so an in-place write would
        // leave the sibling un-dirtied. Loading flags belong to a feed; a typed value is never
        // loading.
        //
        // One object per date matters beyond OnPush: on the All position this writes N records at
        // once, and sharing one instance across them would make a later per-date edit silently
        // rewrite every other date through the alias.
        for (let i = 0; i < targets.length; i++) {
            this.tabOverrides[targets[i]] = copyOverride(next, {edited: true, ivLoading: false, vsLoading: false});
        }
        // CR-20260831-004: override values are run parameters too.
        this.recomputeParamChanges();
    }

    // Clears THIS TAB's override for THIS date only (owner ruling D10). The reading was never
    // stopped, so the boxes fall straight back to the market — no refetch, no spinner, no blank.
    @DetectMethodChanges()
    onOverrideClear(): void {
        // Same targets as an edit: on "All dates" the link clears the whole range, which is the
        // only reading that undoes what the matching edit did.
        const targets = this.overrideWriteTargets();
        if (targets.length === 0) {
            return;
        }
        for (let i = 0; i < targets.length; i++) {
            delete this.tabOverrides[targets[i]];
        }
        // CR-20260831-004: removing an override the run was sent with is a change like any edit.
        this.recomputeParamChanges();
    }

    // NOTE: neither handler touches the live feed. The feed follows the ANCHORED DATE, and editing
    // or clearing does not change which date is anchored — only stepping, switching tab, changing
    // the selection or resetting does, and those all funnel through refreshLiveFeed() already.
    // ---- Live indicator feed (owner ruling 2026-07-22) ---------------------------------------
    //
    // The anchored date's boxes TICK, exactly like the panel's own indicators. Editing does not
    // stop the feed — it puts the user's own record in front of it (D20); Clear removes that record
    // and the market shows through again, already up to date because it never stopped.
    //
    // The feed is keyed by DATE ALONE, never by tab: a VerticalSkewChangedDto carries callSkew and
    // putSkew together, and IV/HS are per date, so one subscription serves every tab looking at
    // that date. Switching tab on the same date therefore churns nothing.
    //
    // Every value comes from a BACKEND service — a query or a push subscription — never from the
    // OPG panel's own fields. Reading the panel's state was how the boxes ended up blank on first
    // open: the panel had not populated itself yet, and sampling a component that is still warming
    // up has no completion event to wait for. A service subscription does.
    //
    // Exactly ONE date is on screen, so exactly ONE feed exists at a time. Everything that can
    // change what is anchored funnels through refreshLiveFeed().

    private _liveKey: string | null = null;
    private _liveIvTimer: any = null;
    private _liveVsSub: Subscription | null = null;
    private _liveHsSub: Subscription | null = null;
    private _liveVsTarget: { ul: string; exp: string } | null = null;

    // The single decision point: which date (if any) should be streaming right now.
    private refreshLiveFeed(): void {
        const key = this.currentOverrideDateKey;

        // Nothing anchored => no feed. There is deliberately NO frozen case any more: an override
        // belongs to one tab, the reading is shared, so stopping the feed because THIS tab edited
        // the date would starve whatever other tab is reading it live (D20).
        if (!key) {
            this.stopLiveFeed();
            return;
        }
        if (this._liveKey === key) {
            return;   // already streaming this date — do not churn the subscription
        }

        this.stopLiveFeed();
        this.startLiveFeed(key);
    }

    private startLiveFeed(key: string): void {
        const ul = this.readSymbolSafe();
        if (!ul) {
            return;
        }

        this._liveKey = key;
        // REPLACE, never mutate — the same rule writeLive follows. The row may already be rendering
        // this reading, and an in-place write leaves that OnPush child un-dirtied: re-anchoring onto
        // a date whose earlier fetch came back empty would raise the loading flags with no spinner
        // to show for it. Replacing the object is what makes the flags reach the screen.
        const prev = this.readingsByDate[key] || makeEmptyOverride();
        this.readingsByDate[key] = copyOverride(prev, {
            edited: false,
            ivLoading: prev.ivCall == null || prev.ivPut == null,
            vsLoading: prev.vsCall == null || prev.vsPut == null
        });

        // IV — query-backed. There is no IV push, so the box is kept live by re-asking on the
        // cache's own TTL; concurrent askers inside a window share one HTTP, so this coalesces with
        // whatever the panel and its siblings are already fetching rather than adding load.
        this.pollLiveIv(ul, key);
        this._liveIvTimer = setInterval(() => this.pollLiveIv(ul, key), LIVE_IV_POLL_MS);

        // VS — a genuine push. Subscribing here is what makes the box tick; the panel's own
        // subscriber filters to ITS selected expiration and so can never serve another date.
        this._liveVsTarget = {ul: ul, exp: key};
        try {
            this._shellClient.processCommand(new SubscribeVerticalSkew(ul, key));
        } catch (e) {
            console.log('[OpgCopilot] VS subscribe failed for ' + key + ': ' + describeError(e));
        }
        this._liveVsSub = this._messageBus.of<VerticalSkewChangedDto>('VerticalSkewChangedDto')
            .subscribe(m => {
                const dto = m ? m.payload : null;
                if (!dto || dto.underlying !== ul || dto.expiration !== key) {
                    return;   // another surface's expiration
                }
                this.writeLive(key, o => {
                    // null stays null: NO USABLE READING, never "flat". A zero here would classify
                    // as Flat and quietly change which structures the engine returns.
                    o.vsCall = typeof dto.callSkew === 'number' ? dto.callSkew : null;
                    o.vsPut = typeof dto.putSkew === 'number' ? dto.putSkew : null;
                    o.vsLoading = !!dto.callLoading || !!dto.putLoading;
                });
            });

        // HS — push, through the service that owns the term-structure subscription. Read once now
        // (it may already hold a ratio) and then follow its change notification. This is the piece
        // that had no completion event before: sampled once at record creation and never refreshed,
        // so a reading that arrived a second later never reached the box.
        // ONE market reading, written into BOTH sides (CR-20260921-002): the sides agree until
        // the trader edits one of them.
        this.writeLive(key, o => {
            o.hsCall = o.hsPut = this._horizontalSkew ? this._horizontalSkew.ratio : null;
        });
        if (this._horizontalSkew) {
            this._liveHsSub = this._horizontalSkew.updated$.subscribe(() =>
                this.writeLive(key, o => { o.hsCall = o.hsPut = this._horizontalSkew.ratio; }));
        }
    }

    private pollLiveIv(ul: string, key: string): void {
        this._ivCache.getIv(ul, key)
            .then(reply => this.writeLive(key, o => {
                o.ivCall = o.ivPut = (reply && typeof reply.volatility === 'number') ? reply.volatility : null;
                o.ivLoading = false;
            }))
            .catch(e => {
                // Never swallow: an empty box looks identical whether the query returned nothing,
                // failed, or was never answered — which is how a completely undeployed backend op
                // once presented as "the feature does not work".
                console.log('[OpgCopilot] IV feed failed for ' + key + ': ' + describeError(e));
                this.writeLive(key, o => { o.ivLoading = false; });
            });
    }

    private stopLiveFeed(): void {
        if (this._liveIvTimer !== null) {
            clearInterval(this._liveIvTimer);
            this._liveIvTimer = null;
        }
        if (this._liveVsSub) {
            this._liveVsSub.unsubscribe();
            this._liveVsSub = null;
        }
        if (this._liveHsSub) {
            this._liveHsSub.unsubscribe();
            this._liveHsSub = null;
        }
        if (this._liveVsTarget) {
            // MANDATORY pairing. The backend ref-counts vertical-skew slots: a slot whose subscriber
            // never unsubscribes is never destroyed and holds its option subscriptions for the life
            // of the process, and an UNPAIRED unsubscribe freezes a sibling surface watching the
            // same legs. Cleared before the call so a throw cannot leave a stale target behind.
            const target = this._liveVsTarget;
            this._liveVsTarget = null;
            try {
                this._shellClient.processCommand(
                    new UnsubscribeVerticalSkew(target.ul, target.exp));
            } catch (e) {
                // Session may already be gone (logout / panel teardown) — the backend's own session
                // cleanup covers that case.
                console.log('[OpgCopilot] VS unsubscribe failed: ' + describeError(e));
            }
        }
        this._liveKey = null;
    }

    // Apply one live tick.
    //
    // Writes the MARKET's side only. Drops silently when the date is no longer the streaming one or
    // after destroy. A tick arriving while the user types can no longer overwrite them: their
    // values live in a different object (the tab's override), so there is nothing here to clobber.
    //
    // REPLACES the record object rather than mutating it. Both duo fieldsets are OnPush and share
    // one reference, so an in-place write leaves them un-dirtied and the value never reaches the
    // screen even though the model is correct.
    private writeLive(key: string, mutate: (o: MarketConditionOverride) => void): void {
        if (this._destroyed || this._liveKey !== key) {
            return;
        }
        const current = this.readingsByDate[key];
        if (!current) {
            return;
        }
        const next = copyOverride(current, {edited: false});   // invariant: a reading is never an override
        mutate(next);
        this.readingsByDate[key] = next;
        this._changeDetector.detectChanges();
    }

    // ---- RUN flow (§6, courier) --------------------------------------------------------------------

    @DetectMethodChanges({isAsync: true})
    async onRun(): Promise<void> {
        if (!this.canRun || !this.root) {
            return;
        }
        const kind = this.activeTab;
        // §6 courier "snapshot of what was sent": clone so the session's sentInputs and the
        // skill request never alias the LIVE working objects (the user can re-expand the
        // questionnaire mid-flight; the widgets are disabled, but the record must not depend
        // on that). Manual clone — the date selection carries Date objects.
        const inputs = cloneTabInputs(this.workingInputs[kind]);

        // (0) Warned clear-on-run (WI-5, owner meeting 2 2026-07-13): a fresh advisory run cannot
        // sanely coexist with whatever is already loaded (either side, hand-built strategies
        // included). If ANYTHING is loaded, warn; on confirm wipe the WHOLE panel and flip every
        // previewed card back across all tabs; on cancel nothing happens (questionnaire edits
        // stay). This is the single sanctioned touch on user-built strategies, and it supersedes
        // both the old append-alongside model and the per-tab re-run preview clearing.
        if (this.root.hasAnyLoadedStrategies()) {
            const clearedPreviews = this.session.loadedTrades.length;   // capture before the clear drains it
            let confirmed = false;
            try {
                await this._clearOnRunConfirm.show(['This Action Will Clear All Loaded Strategies.']);
                confirmed = true;
            } catch {
                confirmed = false;   // rejected (or the confirm was unavailable) — never clear on doubt
            }
            if (!confirmed) {
                console.log('[OpgCopilot] clear-on-run cancelled — nothing cleared ('
                    + clearedPreviews + ' copilot preview(s) still loaded)');
                return;
            }
            this.root.clearAllStrategies();       // host removes every strategy (borders + §8 hooks per strategy)
            this.session.resetAllPreviews();       // drain the registry + flip every previewed card back
            console.log('[OpgCopilot] clear-on-run confirmed — panel cleared ('
                + clearedPreviews + ' copilot preview(s) + any user-built strategies)');
        }

        // (1) Re-Run clears THAT tab's previews FIRST (§7): unload the real strategies + borders
        // for every registry row the reset returns (scoped by tabKind, superseded runs included).
        const removed = this.session.resetTabForReRun(kind);
        for (let i = 0; i < removed.length; i++) {
            this.root.unloadTrade(removed[i].side, removed[i].strategyId);
            this.root.clearBorder(removed[i].strategyId);
        }

        // (2) single-flight acquire
        const run = this.session.startRun(kind, inputs);
        if (!run) {
            return;
        }

        // (2b) CR-20260831-004: this run IS the new parameter baseline for the tab. Snapshot the
        // overrides beside the questionnaire snapshot the session just took (they live outside
        // TabInputs by design), then re-diff — live and sent are equal at this instant, so every
        // param-changed cue on this tab clears. Round 3: it is also now the LAST-RUN tab (the tab
        // cue clears by definition — activeTab === lastRunTab), and the host is told a run fired.
        this._sentOverrides[kind] = this.cloneOverridesMap(this.overridesByTab[kind]);
        this.lastRunTab = kind;
        this.recomputeParamChanges();
        this.runFired.emit();

        // (3) collapse the questionnaire to the breadcrumb bar (frame 03). WI-16: and EXPAND both
        // results columns — a side the user collapsed while reading the previous run's results must
        // not hide the results of the run they just asked for.
        this.questionnaireCollapsed[kind] = true;
        this.resultsSideCollapsed = {calls: false, puts: false};

        try {
            // (4) build the skill request VERBATIM (courier §6 — no filtering/reshaping host-side)
            const request = this.buildSkillRequest(kind, inputs);

            // (4b) WI-15/A6 + CR-20260723-001: a Run READ the chosen ATM and default qty live and sent
            // them, so those two re-baseline to match (their yellow clears). The EXPIRATION is
            // deliberately NOT re-baselined: the run sent the CAPTURED expiration (self-contained), so
            // adopting the panel's live one here would clear the expiration yellow — and drag the
            // indicators onto the panel's date — while the CoPilot is still on its own date. Only a
            // refresh captures a new expiration. MUST come BEFORE the flush below (the sole CD pass until
            // the skill settles), else the breadcrumb paints stale.
            this.reBaselineAtRun();

            // Paint the pending state NOW (family recipe): the isAsync decorator only runs CD after
            // this whole method settles, so without this flush the spinner would never show. Inside
            // the try so even a CD throw routes to completeRun('terminalFailed') and releases the lock.
            this._changeDetector.detectChanges();

            // §11 "what did the skill get asked" — the paired request record for the run log
            // (safe to stringify: the request is this run's own snapshot, never mutated after).
            console.log('[OpgCopilot] run-request ' + run.id + ' ' + JSON.stringify(request));

            // (5) backstop: stuck ≠ empty (§11)
            this.startBackstop(run.id, kind);

            // (6) run the skill; map the wire result to domain RunContents
            const result = await this._skill.run(request);
            this.clearBackstop(run.id);
            const contents: RunContents = {};
            const calls = toSideContents(result ? result.calls : null);
            const puts = toSideContents(result ? result.puts : null);
            if (calls) {
                contents.calls = calls;
            }
            if (puts) {
                contents.puts = puts;
            }
            // CR-20260902-001: the skill's explanation for an empty side, verbatim (courier §6).
            if (result && result.callsNote) {
                contents.callsNote = result.callsNote;
            }
            if (result && result.putsNote) {
                contents.putsNote = result.putsNote;
            }
            const outcome: SettledOutcome = (contents.calls || contents.puts) ? 'succeeded' : 'empty';
            if (this.session.completeRun(run.id, outcome, contents, null)) {
                this.logRun(run.id, kind, outcome);   // (7) §11 run log
            }
        } catch (e) {
            this.clearBackstop(run.id);
            const message = (e && e.message) ? String(e.message) : 'The ORION Run Failed. Please Try Again.';
            if (this.session.completeRun(run.id, 'terminalFailed', {}, message)) {
                this.logRun(run.id, kind, 'terminalFailed');
            }
        }
    }

    private buildSkillRequest(kind: TabKind, inputs: TabInputs): OpgSkillRequest {
        const sides: { calls?: SideInputs; puts?: SideInputs } = {};
        if (kind === 'callsAndPuts') {
            const dual = inputs as DualSideTabInputs;
            sides.calls = dual.calls;
            sides.puts = dual.puts;
        } else if (kind === 'calls') {
            sides.calls = (inputs as SingleSideTabInputs).side;
        } else {
            sides.puts = (inputs as SingleSideTabInputs).side;
        }
        if (sides.calls) { sides.calls = {...sides.calls, presetApgSource: this.presetApgSource('calls')}; }
        if (sides.puts) { sides.puts = {...sides.puts, presetApgSource: this.presetApgSource('puts')}; }
        return {
            tab: kind,
            // CR-20260722-002. THIS TAB's overrides, keyed off the `kind` argument rather than the
            // active tab, so a request always carries the overrides of the tab being run (D20).
            // Every record here is edited by construction; the adapter still filters on `edited` +
            // completeness, so nothing edited => null => the backend measures everything, which is
            // byte-identical to pre-CR behaviour.
            marketOverrides: this.overridesByTab[kind],
            // CR-20260727-001. The user's expected-move MODEL (never a computed value — the skill
            // still resolves the boundary itself). Null when the setting is unusable, which is also
            // the state in which neither Expected Range control is offered, so a run cannot be
            // carrying an expectedRange form without one.
            expectedRangeModel: this._expectedMoveService.getCoPilotRangeModel(),
            // Resolve the UI-only 'loaded' selection to a concrete single date HERE, re-reading the
            // panel's expiration at Run (WI-3) — the skill wire only ever carries single|range.
            date: this.resolveDateForRequest(inputs.date),
            sides,
            context: {
                // ADR-037 (CR-20260716-001): the two wire amendments — the backend cannot
                // resolve chains/IV without the symbol (empty => preSendBlocked), and the
                // per-panel id scopes the backend supersede to THIS panel (ADR-033).
                underlying: this.readSymbolSafe(),
                panelInstanceId: this.session.panelInstanceId,
                // The guarded, memoized getters (never null; fresh-swapped, not mutated in
                // place, so the snapshot request stays immutable).
                expirations: this.availableExpirationKeys,
                // CR-20260723-001: the CAPTURED expiration (self-contained), not a live panel read.
                selectedExpiration: this.capturedLoadedExpirationKey,   // THE ANCHOR (A2)
                strikeLadder: this.strikeLadder,
                chosenAtm: this.root.readChosenAtm(),
                dynamicAtm: this.root.readDynamicAtm(),
                defaultQty: this.root.readDefaultQty(),
                // CR-20260924-002: the host's ES mode (Hedge Matrix only; an absent member = off).
                esMode: this.root.readEsMode ? !!this.root.readEsMode() : false,
                esContracts: this.root.readEsContracts ? (this.root.readEsContracts() || null) : null
            }
        };
    }

    // 'loaded' -> {kind:'single', date:<the panel's selected expiration NOW>} (WI-3). Re-reads the
    // facade at Run so the sent date is the panel's CURRENT expiration, never one cached at open.
    // single|range pass through untouched. canRun has already gated out an unresolvable 'loaded'.
    private resolveDateForRequest(sel: OpgDateSelection): OpgDateSelection {
        if (sel && sel.kind === 'loaded') {
            // CR-20260723-001: a Run sends the CAPTURED expiration (self-contained). The panel's live
            // date reaches the CoPilot ONLY through a refresh — never silently on fire.
            const d = this.capturedLoadedExpirationDate;
            return d ? {kind: 'single', date: d} : sel;
        }
        return sel;
    }

    private startBackstop(runId: string, kind: TabKind): void {
        this.clearBackstop();   // arming replaces any prior timer (single-flight: at most one pending run)
        this._backstopRunId = runId;
        this._backstopHandle = setTimeout(() => {
            this._backstopHandle = null;
            this._backstopRunId = null;
            // Settles the run 'terminalFailed' if still pending; a late skill result after this is
            // discarded by completeRun's isCurrent() check (stuck is NOT empty, §11).
            if (this.session.timeoutRun(runId)) {
                this.logRun(runId, kind, 'terminalFailed');
                this._changeDetector.detectChanges();
            }
        }, this.session.backstopMs);
    }

    // No arg = force-clear (refresh / destroy — NOT window close: A6 keeps a pending run and its
    // backstop alive across a minimize). With a runId, clears ONLY when that run still owns the
    // timer — a superseded run's continuation must not cancel the current run's backstop.
    private clearBackstop(runId?: string): void {
        if (runId !== undefined && this._backstopRunId !== null && this._backstopRunId !== runId) {
            return;
        }
        if (this._backstopHandle) {
            clearTimeout(this._backstopHandle);
        }
        this._backstopHandle = null;
        this._backstopRunId = null;
    }

    // §11 run log: what was asked / what came back, correlatable by runId. Both CR: the
    // per-side family tally makes a one-family mix visible in the log — Both must never
    // SILENTLY collapse to a single family.
    private logRun(runId: string, kind: TabKind, outcome: string): void {
        const tab = this.session.tab(kind);
        const results = tab ? tab.results : null;
        console.log('[OpgCopilot] run ' + runId + ' tab=' + kind + ' outcome=' + outcome
            + ' calls=' + sideIds(results ? results.calls : null) + sideFamilies(results ? results.calls : null)
            + ' puts=' + sideIds(results ? results.puts : null) + sideFamilies(results ? results.puts : null));
    }

    // ---- Preview / Un-Preview (§7/§8 — real strategies + the X border) ----------------------------

    // State decides the direction. WITHIN one date previews are STICKY (§7 — comparison is the
    // point): previewing another same-date card never un-previews earlier ones. ACROSS dates
    // (A5/WI-6) a preview switches the panel to the trade's bound date, first auto-un-previewing
    // every other-date preview so no loaded column is ever stranded at a foreign date.
    @DetectMethodChanges()
    onPreviewToggled(suggestion: OpgTradeSuggestion): void {
        if (!this.root || !suggestion) {
            return;
        }
        if (suggestion.state.previewed) {
            this.unPreviewOne(suggestion);
            return;
        }
        this.previewWithNavigation(suggestion);
    }

    // Load All (A5 unified members list; WI-7 gates + navigates once): the Load-All bar only
    // enables for a single-expiration remaining set, so load every not-yet-previewed member in one
    // move — switching the panel to their shared date at most once (WI-6 rules: auto-un-preview
    // every OTHER-date preview FIRST so no foreign border sits at the new date), then ONE
    // applySideIfLive per touched side (Live prices the whole batch in one pass).
    @DetectMethodChanges()
    onLoadAllRequested(members: OpgTradeSuggestion[]): void {
        if (!this.root || !members) {
            return;
        }
        // The Load-All bar is a COMPARISON affordance — hidden under 'pick' (one hedge per
        // date/side); this guard is the belt to that *ngIf's braces.
        if (this.previewModel === 'pick') {
            console.log('[OpgCopilot] load-all suppressed — pick preview model');
            return;
        }
        const pending = unPreviewedMembers(members);
        if (pending.length === 0) {
            return;
        }
        // The single-date grid cannot hold mixed expirations. The bar is [disabled] for such sets
        // (WI-7), but re-check here so we never load a mixed set even if a stale UI state let the
        // click through — same distinct-count function the gate uses, so they can't disagree
        // (defense-in-depth, mirroring the WI-6 nav-abort path).
        if (distinctUnpreviewedExpirationCount(pending) > 1) {
            console.log('[OpgCopilot] load-all REFUSED — remaining members span multiple'
                + ' expirations (the UI gate should have blocked this)');
            return;
        }
        // Cross-date set → ONE navigate before loading (auto-un-preview other-date previews first,
        // exactly as a per-card cross-date preview does); same-date set → straight to the load loop.
        const targetDate = pending[0].content.expiration;
        const panelDate = this.root.readSelectedExpirationDate();
        if (targetDate && targetDate !== panelDate) {
            const autoRemoved = this.unPreviewOtherDates(targetDate);
            if (!this.root.navigateToExpiration(targetDate)) {
                console.log('[OpgCopilot] load-all preview-nav to ' + targetDate + ' FAILED'
                    + ' (unknown/invisible) — aborted; auto-un-previewed [' + autoRemoved.join(',') + ']');
                return;
            }
            this.reBaselineNavigatedExpiration(targetDate);   // WI-15: our own write is not user drift
            console.log('[OpgCopilot] load-all preview-nav to ' + targetDate
                + '; auto-un-previewed [' + autoRemoved.join(',') + ']');
        }
        const touchedSides: OpgSide[] = [];
        for (let i = 0; i < pending.length; i++) {
            this.loadRegisterAndBorder(pending[i]);
            if (touchedSides.indexOf(pending[i].content.side) < 0) {
                touchedSides.push(pending[i].content.side);
            }
        }
        for (let i = 0; i < touchedSides.length; i++) {
            this.root.applySideIfLive(touchedSides[i]);
        }
    }

    // Un-Preview All: remove exactly the previewed members' own instances (identity = minted
    // strategyId, §9 — never name/legs).
    @DetectMethodChanges()
    onUnPreviewAllRequested(members: OpgTradeSuggestion[]): void {
        if (!this.root || !members) {
            return;
        }
        for (let i = 0; i < members.length; i++) {
            if (members[i].state.previewed) {
                this.unPreviewOne(members[i]);
            }
        }
    }

    // A5/WI-6: preview a trade that may be bound to a DIFFERENT date than the panel currently
    // shows. Cross-date → (1) auto-un-preview every OTHER-date loaded trade (clearing its border)
    // so the "X" invariant holds across the switch, (2) navigate the panel to the trade's bound
    // date — the ONLY expiration change the CoPilot causes; abort the preview if it can't, (3) load
    // as usual. Same-date → straight to load (sticky comparison, no navigation).
    private previewWithNavigation(suggestion: OpgTradeSuggestion): void {
        const targetDate = suggestion.content.expiration;
        if (this.previewModel === 'pick') {
            // 'pick' (owner ruling 2026-08-04): exclusive per (date, side), coexisting across
            // dates — the same-date same-side predecessor is REPLACED by this preview; no
            // navigation and no other-date clearing on the multi-expiration host.
            const replaced = this.unPreviewSameDateSide(targetDate, suggestion.content.side,
                suggestion.id);
            if (replaced.length > 0) {
                console.log('[OpgCopilot] pick-preview at ' + targetDate
                    + ' replaced [' + replaced.join(',') + ']');
            }
            this.loadRegisterAndBorder(suggestion);
            this.root.applySideIfLive(suggestion.content.side);
            return;
        }
        const panelDate = this.root.readSelectedExpirationDate();
        if (targetDate && targetDate !== panelDate) {
            // (1) clear other-date previews + borders FIRST — no foreign border may sit at the new
            // panel date even for an instant (the done-check invariant).
            const autoRemoved = this.unPreviewOtherDates(targetDate);
            // (2) switch the panel to the trade's bound date.
            if (!this.root.navigateToExpiration(targetDate)) {
                console.log('[OpgCopilot] preview-nav to ' + targetDate + ' FAILED (unknown/invisible)'
                    + ' — aborted for suggestion=' + suggestion.id
                    + '; auto-un-previewed [' + autoRemoved.join(',') + ']');
                return;   // leave the card un-previewed
            }
            this.reBaselineNavigatedExpiration(targetDate);   // WI-15: our own write is not user drift
            console.log('[OpgCopilot] preview-nav to ' + targetDate + ' for suggestion=' + suggestion.id
                + '; auto-un-previewed [' + autoRemoved.join(',') + ']');
        }
        // (3) load + border + live-apply exactly as a same-date preview.
        this.loadRegisterAndBorder(suggestion);
        this.root.applySideIfLive(suggestion.content.side);
    }

    // Un-preview (unload + clear border + flip card) every loaded trade whose bound expiration
    // differs from targetDate — the set the session identifies purely (loadedTradesOnOtherDates).
    // Returns the un-previewed suggestion ids for the navigation log. Iterates the session's
    // SNAPSHOT, so unPreviewOne's registry mutation is safe mid-walk.
    private unPreviewOtherDates(targetDate: string): string[] {
        const rows = this.session.loadedTradesOnOtherDates(targetDate);
        const removedIds: string[] = [];
        for (let i = 0; i < rows.length; i++) {
            const row = rows[i];
            const suggestion = this.session.findSuggestion(row.suggestionId);
            if (suggestion) {
                this.unPreviewOne(suggestion);
            } else {
                // Defensive: a registry row whose card is gone — unload by strategyId directly.
                this.root.unloadTrade(row.side, row.strategyId);
                this.root.clearBorder(row.strategyId);
                this.session.unregisterByStrategyId(row.strategyId);
            }
            removedIds.push(row.suggestionId);
        }
        return removedIds;
    }

    // The 'pick' replacement set (owner ruling 2026-08-04): every loaded trade sharing this
    // bound date AND side, conversation-wide (the Matrix holds one pick per date/side no matter
    // which copilot tab placed it). Walks the session's snapshot copy, so unPreviewOne's
    // registry mutation is safe mid-walk — the unPreviewOtherDates pattern.
    private unPreviewSameDateSide(targetDate: string, side: OpgSide,
                                  exceptSuggestionId: string): string[] {
        const rows = this.session.loadedTrades;
        const removedIds: string[] = [];
        for (let i = 0; i < rows.length; i++) {
            const row = rows[i];
            if (row.expiration !== targetDate || row.side !== side
                || row.suggestionId === exceptSuggestionId) {
                continue;
            }
            const suggestion = this.session.findSuggestion(row.suggestionId);
            if (suggestion) {
                this.unPreviewOne(suggestion);
            } else {
                // Defensive: a registry row whose card is gone — unload by strategyId directly.
                this.root.unloadTrade(row.side, row.strategyId);
                this.root.clearBorder(row.strategyId);
                this.session.unregisterByStrategyId(row.strategyId);
            }
            removedIds.push(row.suggestionId);
        }
        return removedIds;
    }

    // Preview steps (a)..(e): load the REAL strategy (host mints the strategyId), register the
    // §9 identity row, set the X border, flip the card state, log the §11 click-trail line.
    private loadRegisterAndBorder(suggestion: OpgTradeSuggestion): void {
        const content = suggestion.content;
        const strategyId = this.root.loadTrade(content.side, content);
        const runId = this.currentRunIdOfActiveTab();
        this.session.registerLoadedTrade({
            suggestionId: suggestion.id,
            runId: runId,
            tabKind: this.activeTab,
            strategyId: strategyId,
            side: content.side,
            startingStrike: content.startingStrike,
            expiration: content.expiration
        });
        this.root.setBorder(strategyId, content.side, content.startingStrike);
        suggestion.state.previewed = true;
        console.log('[OpgCopilot] preview run=' + runId + ' suggestion=' + suggestion.id
            + ' strategy=' + strategyId + ' strike=' + content.startingStrike);
    }

    // Un-Preview = remove (§7): unload exactly this suggestion's own loaded instance + its border.
    private unPreviewOne(suggestion: OpgTradeSuggestion): void {
        const row = this.session.unregisterBySuggestionId(suggestion.id);
        if (row) {
            this.root.unloadTrade(row.side, row.strategyId);
            this.root.clearBorder(row.strategyId);
            console.log('[OpgCopilot] un-preview run=' + row.runId + ' suggestion=' + row.suggestionId
                + ' strategy=' + row.strategyId + ' strike=' + row.startingStrike);
        }
        suggestion.state.previewed = false;
    }

    private currentRunIdOfActiveTab(): string {
        const tab = this.session.tab(this.activeTab);
        return (tab && tab.currentRun) ? tab.currentRun.id : '';
    }

    // ---- stale-grid baseline (WI-15 / A6) -----------------------------------------------------------

    // Re-baseline: "the CoPilot is now in sync with the grid." Called at the TWO moments the CoPilot
    // actually reads the panel — conversation start (first open + refresh) and Run fire — so the
    // yellow means exactly one thing: the user moved the grid BETWEEN those moments.
    private captureGridSync(trigger: string): void {
        this._capturedGrid = this.liveGridFingerprint;
        this._capturedLoadedDateText = this.liveLoadedDateText;
        this._lastLoggedDrift = '';
        console.log('[OpgCopilot] grid baseline captured (' + trigger + ')'
            + ' expiration=' + this._capturedGrid.expiration
            + ' chosenAtm=' + this._capturedGrid.chosenAtm
            + ' defaultQty=' + this._capturedGrid.defaultQty);
        this._emitStale();   // a fresh baseline clears any drift => drop the launcher cue
    }

    // CR-20260723-001: the Run-fire PARTIAL sync. A Run reads the chosen ATM and default qty live and
    // sends them, so re-baseline exactly those two — their yellow means "moved since the last sync", and
    // the run WAS a sync for them. The EXPIRATION is left on its captured value on purpose: a Run sends
    // the captured expiration, never the panel's live one, so adopting the live expiration here would
    // turn off the expiration yellow (and drag the indicators onto the panel's date) while the CoPilot
    // is still anchored to its own date. Refresh is the only expiration capture point now.
    private reBaselineAtRun(): void {
        if (!this._capturedGrid) {
            return;
        }
        const live = this.liveGridFingerprint;
        this._capturedGrid = makeGridFingerprint(this._capturedGrid.expiration, live.chosenAtm, live.defaultQty);
        this._lastLoggedDrift = '';
        this._emitStale();
    }

    // The CoPilot's OWN expiration write (WI-6 preview navigation) landed. Re-baseline the
    // EXPIRATION MEMBER ONLY — the panel is now exactly where we put it, so this is not drift; but a
    // chosen-ATM / qty / symbol drift the user made must SURVIVE our navigation (a whole-fingerprint
    // re-capture here would silently absorb it and turn the yellow off behind their back).
    private reBaselineNavigatedExpiration(dateKey: string): void {
        if (!this._capturedGrid) {
            return;
        }
        this._capturedGrid = withExpiration(this._capturedGrid, dateKey);
        this._capturedLoadedDateText = this.liveLoadedDateText;
        this._lastLoggedDrift = '';
        this._emitStale();
    }

    // §11: log every stale TRANSITION (which member, captured vs live) — once per distinct drift
    // set, never per CD pass.
    private logDriftTransition(members: OpgGridMember[], live: OpgGridFingerprint): void {
        const key = members.join(',');
        if (key === this._lastLoggedDrift) {
            return;
        }
        this._lastLoggedDrift = key;
        if (members.length === 0) {
            console.log('[OpgCopilot] grid stale cleared — the panel matches the captured state again');
            return;
        }
        const parts: string[] = [];
        for (let i = 0; i < members.length; i++) {
            parts.push(members[i]
                + ' captured=' + gridMemberValue(this._capturedGrid, members[i])
                + ' live=' + gridMemberValue(live, members[i]));
        }
        console.log('[OpgCopilot] grid stale — ' + parts.join('; '));
    }

    // Push the flag to the host so it can paint the collapsed FAB. Deduped (real transitions only);
    // over-calling is harmless, which is why every capture point calls it.
    // CR-20260831-004: the FAB cue is now a UNION — grid drift OR any parameter changed since the
    // tab's current run ("if any parameters are highlighted yellow, the collapsed button also must
    // be yellow", owner ruling 2026-08-31).
    private _emitStale(): void {
        const grid = this.isGridStale;
        if (grid !== this._lastEmittedGridStale) {
            this._lastEmittedGridStale = grid;
            this.gridStaleChange.emit(grid);
        }
        // Round 3: the tab cue joins the FAB union (owner Q1: "should light — yellow is the
        // indication that what is selected now is not what has been ran").
        const stale = grid || this.hasParamChanges || this.activeTabNotLastRun;
        if (stale === this._lastEmittedStale) {
            return;
        }
        this._lastEmittedStale = stale;
        this.staleChange.emit(stale);
    }

    // ---- parameter-change-vs-run recompute (CR-20260831-004) ---------------------------------

    // ANY tab holds a changed-since-run parameter (override boxes included). Feeds the FAB union —
    // the whole conversation is one dialog, so a collapsed window must report a change on any tab.
    get hasParamChanges(): boolean {
        return hasAnyChange(this.paramChanges.calls)
            || hasAnyChange(this.paramChanges.puts)
            || hasAnyChange(this.paramChanges.callsAndPuts);
    }

    // Re-diff every tab's LIVE inputs against its current run's baseline and refresh every derived
    // view the templates bind. Called from every input mutation point, from Run fire (both sides of
    // the diff are then equal => the maps empty), and from the conversation reset (baselines gone
    // => the maps empty). Fresh objects throughout — the OnPush children wake on the references.
    private recomputeParamChanges(): void {
        const tabs: TabKind[] = ['calls', 'puts', 'callsAndPuts'];
        const next: { [key: string]: ParamChangeMap } = {};
        for (let i = 0; i < tabs.length; i++) {
            const kind = tabs[i];
            const tab = this.session.tab(kind);
            const run = tab ? tab.currentRun : null;
            const map = diffTabInputs(run ? run.sentInputs : null, this.workingInputs[kind]);
            if (run && this._sentOverrides[kind]) {
                const ovr = diffOverrides(this._sentOverrides[kind], this.overridesByTab[kind]);
                for (const k in ovr) {
                    if (ovr.hasOwnProperty(k)) {
                        map[k] = true;
                    }
                }
            }
            next[kind] = map;
        }
        this.paramChanges = {calls: next['calls'], puts: next['puts'], callsAndPuts: next['callsAndPuts']};
        this.sideParamChanges = {
            calls: subMap(next['calls'], 'side.'),
            puts: subMap(next['puts'], 'side.'),
            duoCalls: subMap(next['callsAndPuts'], 'calls.'),
            duoPuts: subMap(next['callsAndPuts'], 'puts.')
        };
        this.currentOverrideChanged = this.buildCurrentOverrideChanged();
        this._emitStale();
    }

    // The tab's SENT date — the current run's baseline the date block computes its own cues from
    // (round 4). Reference-stable per run (sentInputs is a frozen snapshot), so the OnPush block
    // wakes exactly when a new run lands or the conversation resets.
    dateBaseline(tab: TabKind): OpgDateSelection | null {
        const t = this.session.tab(tab);
        return (t && t.currentRun) ? t.currentRun.sentInputs.date : null;
    }

    // The anchored date's changed override boxes on the active tab, or null when clean — resolved
    // here because the fieldset shows ONE date at a time and the child should not know the key
    // grammar. Recomputed on every recompute AND on anchor moves (arrows / tab switch / date edit).
    // UNION on the "All dates" position (owner ruling 2026-09-17): a box is flagged if ANY date in
    // the range no longer matches what the run was sent.
    //
    // Once an All-dates edit has stamped every date with the same record, the union and the
    // per-date answer are IDENTICAL — they diverge only when per-date edits have been mixed into
    // the range, and there the union is exactly the warning wanted. The alternative, showing
    // nothing on the aggregate position, would make a changed parameter invisible on the position
    // the dialog now OPENS in: a missing warning reads the same as "nothing is wrong".
    //
    // Range scope on a per-box surface does not collide with D21's split here, because the All
    // position has no competing per-date cue — the position IS the range.
    private buildCurrentOverrideChanged(): OverrideChangedKeys | null {
        return readOverrideChangedKeys(this.paramChanges[this.activeTab], this.overrideChangedScopeKeys());
    }

    private overrideChangedScopeKeys(): string[] {
        if (this.isAllDatesAnchor) {
            return this.overrideDateKeys;
        }
        const key = this.currentOverrideDateKey;
        return key ? [key] : [];
    }

    // Deep-copy the tab's user-supplied override records at Run fire — the same "snapshot of what
    // was sent" rule cloneTabInputs applies to the questionnaire (§6 courier).
    private cloneOverridesMap(map: MarketConditionOverrides): MarketConditionOverrides {
        const out: MarketConditionOverrides = {};
        for (const k in map) {
            if (map.hasOwnProperty(k)) {
                out[k] = copyOverride(map[k]);
            }
        }
        return out;
    }

    // ---- conversation reset -------------------------------------------------------------------------

    private startFreshConversation(symbol: string): void {
        this.session.startConversation(symbol);
        this.workingInputs = this.makeDefaultWorkingInputs();
        // CR-20260722-002: overrides are conversation state — the refresh button resets the WHOLE
        // dialog (owner ruling 2026-07-14), and stale conditions surviving a symbol change would
        // silently apply another underlying's numbers.
        //
        // CR-20260723-001: stop the live feed BEFORE clearing readingsByDate. The refresh button
        // keeps the panel's loaded expiration, so the anchored date is UNCHANGED across the reset;
        // without this, refreshLiveFeed()'s `_liveKey === key` short-circuit never rebuilds the
        // reading object we just deleted, and writeLive() bails for the same reason. The boxes then
        // stay permanently blank — and a null `value` makes the override row drop every keystroke
        // (isUserEdit needs a base record to promote), so typed overrides are silently ignored too.
        // Resetting the feed here forces a full re-seed + re-subscribe on the reanchorOverrides()
        // below. Safe on a never-started feed (every handle is null-guarded).
        this.stopLiveFeed();
        this.readingsByDate = {};
        this.overridesByTab = {calls: {}, puts: {}, callsAndPuts: {}};
        // CR-20260831-004: refresh = full reset of the run baseline ("the state of the run also
        // must be reset as if nothing has been applied", owner ruling 2026-08-31). The session's
        // runs are already gone (endConversation), so with the override snapshots dropped the
        // recompute empties every param-changed cue — and nothing re-flags until the next Run.
        this._sentOverrides = {calls: null, puts: null, callsAndPuts: null};
        this.lastRunTab = null;   // round 3: no runs => no "not the last-run tab" cue either
        this.recomputeParamChanges();
        // CR-20260723-001: capture the grid baseline BEFORE reanchorOverrides(). The override block now
        // anchors to the CAPTURED expiration (self-contained), so _capturedGrid must be set before the
        // first seed — otherwise the block resolves a null date and stays blank at open.
        this.captureGridSync('conversation');   // WI-15: the new conversation is in sync by definition
        this.reanchorOverrides();   // always-visible block must be filled from the first paint
        this.questionnaireCollapsed = {calls: false, puts: false, callsAndPuts: false};
        this.sideCollapsed = {calls: false, puts: false};
        this.resultsSideCollapsed = {calls: false, puts: false};   // WI-16
        this.duoLockedHeightPx = null;   // both sides expand -> natural sizing again (WI-12)
        // Recreate the questionnaire children so they reset to the fresh inputs (family recipe).
        this.contentVisible = false;
        this._changeDetector.detectChanges();
        Promise.resolve().then(() => {
            if (this._destroyed) {
                return;
            }
            this.contentVisible = true;
            this._changeDetector.detectChanges();
            this._scheduleContentMeasure();
        });
    }

    private readSymbolSafe(): string {
        return (this.root ? this.root.readSymbol() : null) || '';
    }

    private widthModeFor(kind: TabKind): 'normal' | 'wide' {
        return kind === 'callsAndPuts' ? 'wide' : 'normal';
    }

    private makeDefaultWorkingInputs(): WorkingInputs {
        return {
            calls: {date: this.makeDefaultDate(), side: this.makeDefaultSideInputs()},
            puts: {date: this.makeDefaultDate(), side: this.makeDefaultSideInputs()},
            callsAndPuts: {
                date: this.makeDefaultDate(),
                calls: this.makeDefaultSideInputs(),
                puts: this.makeDefaultSideInputs()
            }
        };
    }

    // Default = 'loaded' (WI-3): the dialog opens with the panel's currently-loaded date already
    // selected, so Run is date-valid without touching the Date section. Single Date / Date Range
    // are user overrides.
    private makeDefaultDate(): OpgDateSelection {
        return {kind: 'loaded'};
    }

    // Structurally valid shape, incomplete values — the Run stays gated until the user fills the
    // form (isTabComplete). Defaults mirror the sketch's control order (frame 02). The default
    // priceRange is NOT follow-through-only, so the non-null maxRisk is the consistent pair
    // (a follow-through-only side carries maxRisk: null — hide-risk-for-follow-through,
    // meeting 2026-07-15 pkg-03; the side component's writeMaxRisk keeps that invariant).
    private makeDefaultSideInputs(): SideInputs {
        return {
            priceRange: {kind: 'strikes', startStrike: null, endStrike: null},
            // normalized: TRUE by default (vrequest 2026-07-20). THIS is the effective default for
            // the Minimum Profit mode — the side component's own `minProfitNormalized = true` is
            // overwritten from here by hydrateFromInputs() on every sync, so the two must stay in step.
            minProfit: {amount: null, normalized: true},
            maxRisk: {kind: 'relative', level: 'Low'}
        };
    }

    // ---- content height (APG donor recipe) ------------------------------------------------------------

    // Coalesce ResizeObserver bursts (popup open animation, collapse/expand transitions) into one
    // measure per frame. The measure skips the hidden state (kept-mounted popup => offsetHeight 0)
    // and unchanged repeats.
    private _scheduleContentMeasure(): void {
        if (this._contentHeightRafId !== null) {
            return;
        }
        this._contentHeightRafId = requestAnimationFrame(() => {
            this._contentHeightRafId = null;
            this._measureAndEmitContentHeight();
        });
    }

    private _measureAndEmitContentHeight(): void {
        const el = this._scrollContentEl;
        if (!el) {
            return;
        }
        const h = el.offsetHeight;
        if (h <= 0 || h === this._lastEmittedContentHeight) {
            return;   // hidden (kept-mounted popup) or unchanged
        }
        this._lastEmittedContentHeight = h;
        this._zone.run(() => this.contentHeightChange.emit(h));
    }
}

// ---- module-level helpers (ES5-safe, no Angular) ----------------------------------------------------

// Map a wire side result to the domain SideTradeContents — VERBATIM, no reshaping (§6).
// A zero-trade side is that side's legitimate "no trades" — treated as ABSENT so it flows
// into the per-side empty note / overall 'empty' outcome instead of rendering a blank
// column indistinguishable from breakage (§6/§11).
function toSideContents(side: OpgSkillSideResult | null | undefined): SideTradeContents | null {
    if (!side) {
        return null;
    }
    const trades = side.trades || [];
    if (trades.length === 0) {
        return null;
    }
    return {trades};
}

// ---- §6 courier snapshot clone (manual — Date objects; a JSON round-trip corrupts them) ----

function cloneDateValue(d: Date | null): Date | null {
    return d instanceof Date ? new Date(d.getTime()) : null;
}

function cloneDateSelection(sel: OpgDateSelection): OpgDateSelection {
    if (!sel) {
        return sel;
    }
    if (sel.kind === 'loaded') {
        return {kind: 'loaded'};   // no fields — but MUST branch before the range fallback below
    }
    if (sel.kind === 'single') {
        return {kind: 'single', date: cloneDateValue(sel.date)};
    }
    return {kind: 'range', start: cloneDateValue(sel.start), end: cloneDateValue(sel.end)};
}

// Deep-clone the atmOffset secondary choice (WI-4) so the courier snapshot never aliases the
// live sub object (matches the full-rebuild discipline the rest of cloneSideInputs follows).
// Every bounded kind must branch BEFORE the followThrough fallback — this family's recorded
// silent-misclone class (CR-20260715-001 lesson): a bare else would quietly rewrite an
// unhandled variant into a Follow-Through.
function cloneAtmSub(sub: OpgAtmOffsetSub): OpgAtmOffsetSub {
    if (sub && sub.kind === 'range') {
        return {kind: 'range', value: cloneAtmRangeValue(sub.value)};
    }
    if (sub && sub.kind === 'both') {
        return {kind: 'both', value: cloneAtmRangeValue(sub.value)};
    }
    return {kind: 'followThrough'};
}

// Same discipline one level down (Both CR): the dynamic variant must branch explicitly —
// falling through to 'dollar' would silently turn "use the expected range" into a NaN extent.
function cloneAtmRangeValue(v: OpgAtmRangeValue): OpgAtmRangeValue {
    if (v && v.mode === 'expectedRange') {
        return {mode: 'expectedRange'};
    }
    return {mode: 'dollar', extent: (v && v.mode === 'dollar') ? v.extent : NaN};
}

function cloneSideInputs(side: SideInputs): SideInputs {
    if (!side) {
        return side;
    }
    let priceRange: OpgPriceRange = side.priceRange;
    if (priceRange) {
        if (priceRange.kind === 'strikes') {
            priceRange = {kind: 'strikes', startStrike: priceRange.startStrike, endStrike: priceRange.endStrike};
        } else if (priceRange.kind === 'followThrough') {
            // CR-20260727-002: the start strike is the whole variant — the side carries direction.
            priceRange = {kind: 'followThrough', startStrike: priceRange.startStrike};
        } else if (priceRange.kind === 'expectedRange') {
            // CR-20260715-001: the fact + the start strike (courier — nothing else exists to carry).
            priceRange = {kind: 'expectedRange', startStrike: priceRange.startStrike};
        } else {
            priceRange = {kind: 'atmOffset', anchor: priceRange.anchor, offset: priceRange.offset, sub: cloneAtmSub(priceRange.sub)};
        }
    }
    // null (follow-through-only side — the risk question does not exist) passes through
    // untouched: the §6 snapshot and the wire must carry the ABSENCE, exactly as the live
    // model does (hide-risk-for-follow-through, meeting 2026-07-15 pkg-03).
    let maxRisk: OpgMaxRisk | null = side.maxRisk;
    if (maxRisk) {
        maxRisk = maxRisk.kind === 'dollar'
            ? {kind: 'dollar', amount: maxRisk.amount, normalized: maxRisk.normalized}
            : {kind: 'relative', level: maxRisk.level};
    }
    return {
        mode: side.mode, presetId: side.presetId, presetValid: side.presetValid, presetName: side.presetName, presetAnchor: side.presetAnchor,
        priceRange,
        minProfit: side.minProfit
            ? {amount: side.minProfit.amount, normalized: side.minProfit.normalized}
            : side.minProfit,
        maxRisk
    };
}

function cloneTabInputs(inputs: TabInputs): TabInputs {
    if (isDualSideInputs(inputs)) {
        return {
            date: cloneDateSelection(inputs.date),
            calls: cloneSideInputs(inputs.calls),
            puts: cloneSideInputs(inputs.puts)
        };
    }
    const single = inputs as SingleSideTabInputs;
    return {date: cloneDateSelection(single.date), side: cloneSideInputs(single.side)};
}

// One fingerprint member's value, for the §11 stale log (WI-15). null/'' print as 'none' so a
// "captured=none" line can never be misread as the string "null" the panel actually holds.
function gridMemberValue(fp: OpgGridFingerprint | null, member: OpgGridMember): string {
    if (!fp) {
        return 'none';
    }
    const v = fp[member];
    return (v === null || v === '') ? 'none' : String(v);
}

// Suggestion ids of one side, for the §11 run log.
function sideIds(side: SideResult | null | undefined): string {
    if (!side || !side.members) {
        return '[]';
    }
    return '[' + side.members.map(t => t.id).join(',') + ']';
}

// Per-side family tally for the §11 run log (Both CR). Empty string for a single-family run
// (no member carries a tradeType — the log line stays byte-identical to before); a tagged
// (Both) result appends " families(range:N followThrough:M)" so a family that came back
// EMPTY is visible as its zero, never mistaken for "Both = the other family".
function sideFamilies(side: SideResult | null | undefined): string {
    if (!side || !side.members || side.members.length === 0) {
        return '';
    }
    let range = 0;
    let followThrough = 0;
    for (let i = 0; i < side.members.length; i++) {
        const content = side.members[i] ? side.members[i].content : null;
        if (content && content.tradeType === 'range') {
            range++;
        } else if (content && content.tradeType === 'followThrough') {
            followThrough++;
        }
    }
    if (range === 0 && followThrough === 0) {
        return '';
    }
    return ' families(range:' + range + ' followThrough:' + followThrough + ')';
}

// Stable empty ladder for the no-chain state (a fresh [] per CD pass would churn OnPush inputs).
const EMPTY_LADDER: number[] = [];

// Family date format for the 'loaded'-date button + breadcrumb chip ("Tue 14-Jul-26") — the APG
// breadcrumb's DOW3 dd-MMM-yy, duplicated per the isolated-sibling rule (never imported). Hoisted,
// so the loadedDateText getter above may call it despite the textual order.
const LOADED_DATE_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const LOADED_DATE_DOW3 = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function formatDowDate(d: Date): string {
    const p2 = (n: number) => (n < 10 ? '0' + n : '' + n);
    return LOADED_DATE_DOW3[d.getDay()] + ' '
        + p2(d.getDate()) + '-' + LOADED_DATE_MONTHS[d.getMonth()] + '-' + p2(d.getFullYear() % 100);
}

// Inverse of copilot-shared/expirationDayKey, kept LOCAL to the sibling (NOT a shared export — so
// the build never has to resolve a fresh cross-module symbol on an already-cached leaf, the exact
// failure that blocked WI-3). Parses a "yyyy-MM-dd" chain key to a LOCAL-midnight Date (local Y/M/D,
// mirroring expirationDayKey so key->Date->key round-trips). Null for a malformed key or one that
// rolls over (month 13, day 32) — a bad key must never silently become a wrong date.
function parseExpirationDayKey(key: string | null): Date | null {
    if (!key || typeof key !== 'string') {
        return null;
    }
    const parts = key.split('-');
    if (parts.length !== 3) {
        return null;
    }
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);
    if (!isFinite(y) || !isFinite(m) || !isFinite(day)) {
        return null;
    }
    const d = new Date(y, m - 1, day);
    if (d.getFullYear() !== y || d.getMonth() !== m - 1 || d.getDate() !== day) {
        return null;
    }
    return d;
}

function sameList(a: any[], b: any[]): boolean {
    if (a.length !== b.length) {
        return false;
    }
    for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) {
            return false;
        }
    }
    return true;
}
